<template>
  <div class="workflow-builder">
    <!-- Header -->
    <header class="header">
      <div class="header-content">
        <div class="header-left">
          <button class="back-btn" @click="goBack">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
          </button>
          <span class="header-subtitle">{{ isEditMode ? 'Edit Workflow' : 'Create Workflow' }}</span>
          <span class="header-divider"></span>
          <input
            type="text"
            v-model="workflowName"
            :placeholder="isEditMode ? 'Workflow Name' : 'Enter Workflow Name'"
            class="workflow-name-input"
            @input="setDirty"
          >
        </div>
        <div class="header-actions">
          <button class="btn btn-reset" @click="resetWorkflow" :disabled="isLoading">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"></polyline><polyline points="23 20 23 14 17 14"></polyline><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"></path></svg>
            Reset
          </button>
          <button
            class="btn btn-save"
            :class="{ 'btn-save-active': isDirty }"
            :disabled="!isDirty || isLoading"
            @click="saveWorkflow"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
            {{ isLoading ? 'Saving...' : 'Save Workflow' }}
          </button>
          <button
            v-if="isEditMode && canActivate"
            class="btn btn-activate"
            :disabled="isLoading"
            @click="activateWorkflow"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
            Activate
          </button>
        </div>
      </div>
    </header>

    <!-- Error Display -->
    <ErrorDisplay />

    <div class="main-content">
      <!-- Left Sidebar - Blocks -->
      <div class="left-sidebar">
        <div class="sidebar-tabs">
          <button
            class="tab-button"
            :class="{ active: activeTab === 'triggers' }"
            @click="activeTab = 'triggers'"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
            Triggers
          </button>
          <button
            class="tab-button"
            :class="{ active: activeTab === 'actions' }"
            @click="activeTab = 'actions'"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
            Actions
          </button>
        </div>

        <div class="sidebar-content">
          <!-- Triggers Tab -->
          <div v-if="activeTab === 'triggers'" class="tab-content">
            <div
              v-for="trigger in enabledTriggers"
              :key="trigger.type"
              class="sidebar-item"
              :class="`sidebar-item-${trigger.type}`"
              draggable="true"
              @dragstart="onDragStart($event, trigger)"
            >
              <div class="sidebar-item-icon" v-html="trigger.icon"></div>
              <div class="sidebar-item-label">{{ trigger.label }}</div>
            </div>
            <!-- Disabled Triggers Section -->
            <div v-if="disabledTriggers.length > 0" class="disabled-section">
              <div class="disabled-header">Coming Soon</div>
              <div
                v-for="trigger in disabledTriggers"
                :key="trigger.type"
                class="sidebar-item sidebar-item-disabled"
                :title="(trigger.type === 'mangovoice' && !mangoVoiceEnabled) ? 'Set up Mango Voice in Settings first to use this trigger' : trigger.description"
              >
                <div class="sidebar-item-icon" v-html="trigger.icon"></div>
                <div class="sidebar-item-label">{{ trigger.label }}</div>
                <div v-if="trigger.type === 'mangovoice' && !mangoVoiceEnabled" class="sidebar-item-hint">
                  Set up Mango Voice in Settings first
                </div>
              </div>
            </div>
          </div>

          <!-- Actions Tab -->
          <div v-if="activeTab === 'actions'" class="tab-content">
            <div
              v-for="action in enabledActions"
              :key="action.type"
              class="sidebar-item"
              :class="`sidebar-item-${action.type}`"
              draggable="true"
              @dragstart="onDragStart($event, action)"
            >
              <div class="sidebar-item-icon" v-html="action.icon"></div>
              <div class="sidebar-item-label">{{ action.label }}</div>
            </div>
            <!-- Disabled Actions Section -->
            <div v-if="disabledActions.length > 0" class="disabled-section">
              <div class="disabled-header">Coming Soon</div>
              <div
                v-for="action in disabledActions"
                :key="action.type"
                class="sidebar-item sidebar-item-disabled"
                :title="action.description"
              >
                <div class="sidebar-item-icon" v-html="action.icon"></div>
                <div class="sidebar-item-label">{{ action.label }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Canvas -->
      <div
        class="canvas"
        ref="canvas"
        @mousedown="startDrag($event, null)"
        @mousemove="onDrag"
        @mouseup="stopDrag"
        @mouseleave="stopDrag"
        @dragover="onDragOver"
        @dragleave="onDragLeave"
        @drop="onDrop"
      >
        <!-- Grid Background -->
        <svg class="grid-background" width="100%" height="100%">
          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#f0f0f0" stroke-width="1"/>
          </pattern>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>

        <!-- Connections SVG -->
        <svg class="connections" width="100%" height="100%">
          <g v-for="connection in connections" :key="connection.id">
            <path
              :d="getConnectionPath(connection)"
              stroke="#a5b4fc"
              stroke-width="2"
              stroke-dasharray="5,5"
              fill="none"
              class="connection-path"
            />
          </g>
        </svg>

        <!-- Connection in progress -->
        <svg v-if="connectionInProgress" class="temp-connection" width="100%" height="100%">
          <path
            :d="getTempConnectionPath()"
            stroke="#6366f1"
            stroke-width="2.5"
            stroke-dasharray="5,5"
            fill="none"
          />
        </svg>

        <!-- Snap Zone Indicator -->
        <div
          v-if="snapTarget"
          class="snap-zone-indicator"
          :style="getSnapIndicatorStyle()"
        >
          <div class="snap-zone-pulse"></div>
          <div class="snap-zone-label">Drop to connect</div>
        </div>

        <!-- Nodes -->
        <div
          v-for="node in nodes"
          :key="node.id"
          class="node"
          :class="[`node-${node.type}`, { 'selected': selectedNode === node.id }]"
          :style="{
            left: `${node.x}px`,
            top: `${node.y}px`,
            backgroundColor: getNodeColor(node.type)
          }"
          @mousedown.stop="startDrag($event, node.id)"
          :data-id="node.id"
        >
          <!-- Delete Button (shows on hover) -->
          <button class="node-delete-btn" @mousedown.stop @click.stop="confirmDeleteNode(node.id)" title="Delete node">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>

          <!-- Node Header -->
          <div class="node-header">
            <div class="node-icon" v-html="getNodeIcon(node.type)"></div>
            <div class="node-title">{{ getNodeLabel(node.type) }}</div>
            <button v-if="nodeHasSettings(node.type)" class="node-menu-btn" @click.stop="openNodeSettings(node)">
              <span class="dots">&#8942;</span>
            </button>
          </div>

          <!-- Node Content Preview -->
          <div class="node-content">
            <div class="node-preview">{{ getNodePreview(node) }}</div>
          </div>

          <!-- Connection Points -->
          <div class="connection-point connection-point-top" @mousedown.stop="startConnection(node.id, 'top')"></div>
          <div class="connection-point connection-point-bottom" @mousedown.stop="startConnection(node.id, 'bottom')"></div>
        </div>

        <!-- Empty State -->
        <div v-if="nodes.length === 0" class="canvas-empty-state">
          <div class="empty-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
          </div>
          <h3>Start building your workflow</h3>
          <p>Drag a trigger from the left panel to get started</p>
        </div>
      </div>
    </div>

    <!-- Settings Sidebar Backdrop (to prevent outer scroll) -->
    <div v-if="showSettings" class="settings-backdrop" @click="closeSettings"></div>

    <!-- Settings Sidebar (Right Panel) -->
    <div class="settings-sidebar" v-if="showSettings" :class="{ 'show': showSettings }">
      <div class="settings-content">
        <!-- Node Type Card Header with Close Button -->
        <div class="node-type-card" :style="{ backgroundColor: getNodeColor(selectedNodeData.type) + '40' }">
          <div class="node-type-icon" :style="{ backgroundColor: getNodeColor(selectedNodeData.type) }" v-html="getNodeIcon(selectedNodeData.type)"></div>
          <div class="node-type-info">
            <span class="node-type-label">{{ getNodeLabel(selectedNodeData.type) }}</span>
            <span class="node-type-description">{{ getNodeDescription(selectedNodeData.type) }}</span>
          </div>
          <button class="close-btn" @click="closeSettings">&times;</button>
        </div>

        <!-- Email Settings -->
        <div class="setting-section" v-if="selectedNodeData.type === 'email'">
          <!-- Feature Not Enabled Notice -->
          <div v-if="!emailFeatureEnabled" class="feature-notice">
            <div class="notice-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
            </div>
            <div class="notice-content">
              <span class="notice-title">Email Not Configured</span>
              <span class="notice-text">This feature requires verified email addresses for your account.</span>
            </div>
          </div>

          <div :class="{ 'feature-disabled': !emailFeatureEnabled }">
            <!--
              From Name + From Email Address are READ-ONLY (2026-05-17). Authoritative
              source is site settings, not per-workflow config:
                - From Name comes from the site's Name field
                - From Email comes from teamio.settings GENERAL_VERIFIED_EMAILS (first entry)
              Backend (utility.js sendEmailWithSendGrid) ignores msgData.fromName/fromEmail
              and looks these values up fresh at send time. Letting users override them
              per-workflow let them save workflows with empty fromEmail → SendGrid 400 in prod.
            -->
            <label>From Name</label>
            <input
              type="text"
              :value="currentSiteName"
              class="form-input"
              disabled
              readonly
              title="From Name is your site's name (configured in site settings)."
            >
            <small class="help-text">Auto-set from your site's name in settings.</small>

            <label class="mt-3">From Email Address</label>
            <input
              type="text"
              :value="firstVerifiedEmail || 'Not configured — contact admin to verify a sender email.'"
              class="form-input"
              :class="{ 'form-input-warning': !firstVerifiedEmail }"
              disabled
              readonly
              title="From Email is your site's verified sender address (GENERAL_VERIFIED_EMAILS in site settings)."
            >
            <small class="help-text">Auto-set from your site's verified sender email. Contact admin to change.</small>

            <!-- For New Patients Only (only for Appointment Scheduled trigger) -->
            <div v-if="currentTrigger === 'APPOINTMENT_SCHEDULED'" class="new-patient-section mt-4">
              <label class="section-heading">For New Patients Only</label>
              <p class="help-text">
                The email will only be sent to the patient when they schedule an appointment
                as a New Patient.
              </p>

              <!-- Mode radio buttons -->
              <div class="new-patient-mode-radios">
                <label class="form-check form-check-inline np-radio-label">
                  <input type="radio" class="form-check-input" value="all"
                    v-model="selectedNodeData.properties.newPatientEmailMode" />
                  <span class="form-check-label-text">For All New Patients <small class="text-muted">(regardless of procedure)</small></span>
                </label>
                <label class="form-check form-check-inline np-radio-label">
                  <input type="radio" class="form-check-input" value="procedure_specific"
                    v-model="selectedNodeData.properties.newPatientEmailMode" />
                  <span class="form-check-label-text">Procedure Specific</span>
                </label>
              </div>

              <!-- ALL MODE: single template + optional provider filter -->
              <div v-if="selectedNodeData.properties.newPatientEmailMode === 'all'" class="new-patient-all-section">
                <div class="np-field-group">
                  <label class="np-field-label">Template</label>
                  <multiselect
                    v-model="selectedNodeData.properties.newPatientEmailTemplateObj"
                    :options="emailTemplates"
                    :searchable="true"
                    :show-labels="false"
                    label="Name"
                    track-by="WikiPageID"
                    placeholder="Select template..."
                    class="workflow-multiselect"
                    @input="(val) => selectedNodeData.properties.newPatientEmailTemplateId = val ? val.SendgridTemplateID : ''"
                  />
                </div>
                <div class="np-field-group">
                  <label class="np-field-label">Provider(s) <small class="text-muted">&mdash; leave empty for Any Provider</small></label>
                  <multiselect
                    v-model="selectedNodeData.properties.newPatientEmailProviderObjs"
                    :options="providersList"
                    :multiple="true"
                    :searchable="true"
                    :close-on-select="false"
                    :show-labels="false"
                    label="displayName"
                    track-by="ProvNum"
                    placeholder="Any Provider"
                    class="workflow-multiselect"
                    @input="(vals) => selectedNodeData.properties.newPatientEmailProviderNums = (vals || []).map(v => v.ProvNum)"
                  />
                </div>
              </div>

              <!-- PROCEDURE SPECIFIC MODE: rules table -->
              <div v-if="selectedNodeData.properties.newPatientEmailMode === 'procedure_specific'" class="new-patient-procedure-section">
                <p class="help-text" style="margin-top: 4px;">
                  Define which template to send based on procedure code and provider.
                </p>
                <div class="rule-table">
                  <div class="rule-header">
                    <div class="col-proc">Proc Code</div>
                    <div class="col-provider">Provider</div>
                    <div class="col-template">Template</div>
                    <div class="col-action"></div>
                  </div>
                  <div
                    v-for="(rule, index) in selectedNodeData.properties.newPatientEmailRules || []"
                    :key="'np-email-rule-' + index"
                    class="rule-row np-rule-row"
                  >
                    <div class="col-proc">
                      <multiselect
                        v-model="rule.procCodes"
                        :options="procCodesList"
                        :multiple="true"
                        :searchable="true"
                        :close-on-select="false"
                        :show-labels="false"
                        label="text"
                        track-by="value"
                        placeholder="Select proc codes..."
                        class="workflow-multiselect workflow-multiselect-sm"
                      />
                    </div>
                    <div class="col-provider">
                      <multiselect
                        v-model="rule.providerObjs"
                        :options="providersList"
                        :multiple="true"
                        :searchable="true"
                        :close-on-select="false"
                        :show-labels="false"
                        label="displayName"
                        track-by="ProvNum"
                        placeholder="Any"
                        class="workflow-multiselect workflow-multiselect-sm"
                        @input="(vals) => rule.providerNums = (vals || []).map(v => v.ProvNum)"
                      />
                    </div>
                    <div class="col-template">
                      <multiselect
                        v-model="rule.templateObj"
                        :options="emailTemplates"
                        :searchable="true"
                        :show-labels="false"
                        label="Name"
                        track-by="WikiPageID"
                        placeholder="Select template..."
                        class="workflow-multiselect workflow-multiselect-sm"
                        @input="(val) => rule.templateId = val ? val.SendgridTemplateID : ''"
                      />
                    </div>
                    <div class="col-action">
                      <button class="remove-rule-btn" @click="removeNewPatientEmailRule(index)">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                      </button>
                    </div>
                  </div>
                  <button class="add-rule-btn" @click="addNewPatientEmailRule">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                    Add Rule
                  </button>
                </div>
              </div>
            </div>

            <!-- Proc Code / Template Mapping -->
            <div class="proc-code-section mt-4">
              <label class="section-heading">Email Rules</label>
              <p class="help-text">Define which template to use based on procedure codes.</p>

              <div class="rule-table">
                <div class="rule-header">
                  <div class="col-proc">Proc Code</div>
                  <div class="col-template">Template</div>
                  <div class="col-sendwhen">Send When</div>
                  <div class="col-action"></div>
                </div>
                <div
                  v-for="(rule, index) in selectedNodeData.properties.emailRules || []"
                  :key="index"
                  class="rule-row"
                >
                  <div class="col-proc">
                    <multiselect
                      v-model="rule.procCodes"
                      :options="procCodesList"
                      :multiple="true"
                      :searchable="true"
                      :close-on-select="false"
                      :show-labels="false"
                      label="text"
                      track-by="value"
                      placeholder="Select proc codes..."
                      class="workflow-multiselect workflow-multiselect-sm"
                    />
                  </div>
                  <div class="col-template">
                    <multiselect
                      v-model="rule.templateObj"
                      :options="emailTemplates"
                      :searchable="true"
                      :show-labels="false"
                      label="Name"
                      track-by="WikiPageID"
                      placeholder="Select template..."
                      class="workflow-multiselect workflow-multiselect-sm"
                      @input="(val) => rule.templateId = val ? val.SendgridTemplateID : ''"
                    />
                  </div>
                  <div class="col-sendwhen">
                    <multiselect
                      v-model="rule.sendWhenObj"
                      :options="emailSendOptions"
                      :searchable="false"
                      :show-labels="false"
                      label="text"
                      track-by="value"
                      placeholder="Select when..."
                      class="workflow-multiselect workflow-multiselect-sm workflow-multiselect-wide-dropdown"
                      @input="(val) => rule.sendWhen = val ? val.value : 'immediately'"
                    />
                  </div>
                  <div class="col-action">
                    <button class="remove-rule-btn" @click="removeEmailRule(index)">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </button>
                  </div>
                </div>
                <button class="add-rule-btn" @click="addEmailRule">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                  Add Rule
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- SMS Settings -->
        <div class="setting-section" v-if="selectedNodeData.type === 'sms'">
          <!-- Feature Not Enabled Notice -->
          <div v-if="!smsFeatureEnabled" class="feature-notice">
            <div class="notice-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
            </div>
            <div class="notice-content">
              <span class="notice-title">{{ smsOutboundOff ? 'Texting Turned Off' : 'SMS Not Configured' }}</span>
              <span class="notice-text">{{ smsOutboundOff ? smsDisabledMessage : 'This feature requires SMS to be set up for your account.' }}</span>
            </div>
          </div>

          <div :class="{ 'feature-disabled': !smsFeatureEnabled }">
            <!-- For New Patients Only (only for Appointment Scheduled trigger) -->
            <div v-if="currentTrigger === 'APPOINTMENT_SCHEDULED'" class="new-patient-section">
              <label class="section-heading">For New Patients Only</label>
              <p class="help-text">
                The SMS will only be sent to the patient when they schedule an appointment
                as a New Patient.
              </p>

              <!-- Mode radio buttons -->
              <div class="new-patient-mode-radios">
                <label class="form-check form-check-inline np-radio-label">
                  <input type="radio" class="form-check-input" value="all"
                    v-model="selectedNodeData.properties.newPatientMode" />
                  <span class="form-check-label-text">For All New Patients <small class="text-muted">(regardless of procedure)</small></span>
                </label>
                <label class="form-check form-check-inline np-radio-label">
                  <input type="radio" class="form-check-input" value="procedure_specific"
                    v-model="selectedNodeData.properties.newPatientMode" />
                  <span class="form-check-label-text">Procedure Specific</span>
                </label>
              </div>

              <!-- ALL MODE: single template + optional provider filter -->
              <div v-if="selectedNodeData.properties.newPatientMode === 'all'" class="new-patient-all-section">
                <div class="np-field-group">
                  <label class="np-field-label">Template</label>
                  <multiselect
                    v-model="selectedNodeData.properties.newPatientSmsTemplateObj"
                    :options="smsTemplates"
                    :searchable="true"
                    :show-labels="false"
                    label="Name"
                    track-by="WikiPageID"
                    placeholder="Select template..."
                    class="workflow-multiselect"
                    @input="(val) => selectedNodeData.properties.newPatientSmsTemplateId = val ? val.WikiPageID : ''"
                  />
                </div>
                <div class="np-field-group">
                  <label class="np-field-label">Provider(s) <small class="text-muted">&mdash; leave empty for Any Provider</small></label>
                  <multiselect
                    v-model="selectedNodeData.properties.newPatientProviderObjs"
                    :options="providersList"
                    :multiple="true"
                    :searchable="true"
                    :close-on-select="false"
                    :show-labels="false"
                    label="displayName"
                    track-by="ProvNum"
                    placeholder="Any Provider"
                    class="workflow-multiselect"
                    @input="(vals) => selectedNodeData.properties.newPatientProviderNums = (vals || []).map(v => v.ProvNum)"
                  />
                </div>
              </div>

              <!-- PROCEDURE SPECIFIC MODE: rules table -->
              <div v-if="selectedNodeData.properties.newPatientMode === 'procedure_specific'" class="new-patient-procedure-section">
                <p class="help-text" style="margin-top: 4px;">
                  Define which template to send based on procedure code and provider.
                </p>
                <div class="rule-table">
                  <div class="rule-header">
                    <div class="col-proc">Proc Code</div>
                    <div class="col-provider">Provider</div>
                    <div class="col-template">Template</div>
                    <div class="col-action"></div>
                  </div>
                  <div
                    v-for="(rule, index) in selectedNodeData.properties.newPatientRules || []"
                    :key="'np-sms-rule-' + index"
                    class="rule-row np-rule-row"
                  >
                    <div class="col-proc">
                      <multiselect
                        v-model="rule.procCodes"
                        :options="procCodesList"
                        :multiple="true"
                        :searchable="true"
                        :close-on-select="false"
                        :show-labels="false"
                        label="text"
                        track-by="value"
                        placeholder="Select proc codes..."
                        class="workflow-multiselect workflow-multiselect-sm"
                      />
                    </div>
                    <div class="col-provider">
                      <multiselect
                        v-model="rule.providerObjs"
                        :options="providersList"
                        :multiple="true"
                        :searchable="true"
                        :close-on-select="false"
                        :show-labels="false"
                        label="displayName"
                        track-by="ProvNum"
                        placeholder="Any"
                        class="workflow-multiselect workflow-multiselect-sm"
                        @input="(vals) => rule.providerNums = (vals || []).map(v => v.ProvNum)"
                      />
                    </div>
                    <div class="col-template">
                      <multiselect
                        v-model="rule.templateObj"
                        :options="smsTemplates"
                        :searchable="true"
                        :show-labels="false"
                        label="Name"
                        track-by="WikiPageID"
                        placeholder="Select template..."
                        class="workflow-multiselect workflow-multiselect-sm"
                        @input="(val) => rule.templateId = val ? val.WikiPageID : ''"
                      />
                    </div>
                    <div class="col-action">
                      <button class="remove-rule-btn" @click="removeNewPatientSmsRule(index)">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                      </button>
                    </div>
                  </div>
                  <button class="add-rule-btn" @click="addNewPatientSmsRule">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                    Add Rule
                  </button>
                </div>
              </div>
            </div>

            <!-- Proc Code / Template / Send When Mapping -->
            <div class="proc-code-section">
              <label class="section-heading">SMS Rules</label>
              <p class="help-text">Define which template to use based on procedure codes.</p>

              <div class="rule-table">
                <div class="rule-header">
                  <div class="col-proc">Proc Code</div>
                  <div class="col-template">Template</div>
                  <div class="col-sendwhen">Send When</div>
                  <div class="col-action"></div>
                </div>
                <div
                  v-for="(rule, index) in selectedNodeData.properties.smsRules || []"
                  :key="index"
                  class="rule-row"
                >
                  <div class="col-proc">
                    <multiselect
                      v-model="rule.procCodes"
                      :options="procCodesList"
                      :multiple="true"
                      :searchable="true"
                      :close-on-select="false"
                      :show-labels="false"
                      label="text"
                      track-by="value"
                      placeholder="Select proc codes..."
                      class="workflow-multiselect workflow-multiselect-sm"
                    />
                  </div>
                  <div class="col-template">
                    <multiselect
                      v-model="rule.templateObj"
                      :options="smsTemplates"
                      :searchable="true"
                      :show-labels="false"
                      label="Name"
                      track-by="WikiPageID"
                      placeholder="Select template..."
                      class="workflow-multiselect workflow-multiselect-sm"
                      @input="(val) => rule.templateId = val ? val.WikiPageID : ''"
                    />
                  </div>
                  <div class="col-sendwhen">
                    <multiselect
                      v-model="rule.sendWhenObj"
                      :options="smsSendOptions"
                      :searchable="false"
                      :show-labels="false"
                      label="text"
                      track-by="value"
                      placeholder="Select when..."
                      class="workflow-multiselect workflow-multiselect-sm workflow-multiselect-wide-dropdown"
                      @input="(val) => rule.sendWhen = val ? val.value : 'immediately'"
                    />
                  </div>
                  <div class="col-action">
                    <button class="remove-rule-btn" @click="removeSmsRule(index)">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </button>
                  </div>
                </div>
                <button class="add-rule-btn" @click="addSmsRule">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                  Add Rule
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Assign Task Settings -->
        <div class="setting-section" v-if="selectedNodeData.type === 'assigntask'">
          <div class="section-header-row">
            <div>
              <label>Task Rules</label>
              <p class="help-text">Define tasks to create based on procedure codes.</p>
            </div>
            <div class="layout-toggle">
              <button
                class="layout-btn"
                :class="{ active: taskRulesLayout === 'vertical' }"
                @click="taskRulesLayout = 'vertical'"
                title="Card Layout"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="3" y1="15" x2="21" y2="15"></line></svg>
              </button>
              <button
                class="layout-btn"
                :class="{ active: taskRulesLayout === 'horizontal' }"
                @click="taskRulesLayout = 'horizontal'"
                title="Table Layout"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="9" y1="21" x2="9" y2="9"></line></svg>
              </button>
            </div>
          </div>

          <!-- Vertical Card Layout -->
          <div v-if="taskRulesLayout === 'vertical'" class="task-rules-cards">
            <div
              v-for="(rule, index) in selectedNodeData.properties.taskRules || []"
              :key="index"
              class="task-rule-card"
            >
              <div class="task-rule-card-header">
                <span class="rule-number">Rule {{ index + 1 }}</span>
                <button class="remove-rule-btn" @click="removeTaskRule(index)">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </button>
              </div>
              <div class="task-rule-card-body">
                <div class="field-row">
                  <div class="field-group" v-if="triggerRequiresProcCode">
                    <label>Proc Codes</label>
                    <multiselect
                      v-model="rule.procCodes"
                      :options="procCodesList"
                      :multiple="true"
                      :searchable="true"
                      :close-on-select="false"
                      :show-labels="false"
                      label="text"
                      track-by="value"
                      placeholder="Select proc codes..."
                      class="workflow-multiselect"
                    />
                  </div>
                  <div class="field-group">
                    <label>Assign To</label>
                    <multiselect
                      v-model="rule.assignees"
                      :options="employeesList"
                      :multiple="true"
                      :searchable="true"
                      :close-on-select="false"
                      :show-labels="false"
                      label="displayName"
                      track-by="EmployeeID"
                      placeholder="Select employees..."
                      class="workflow-multiselect"
                    />
                  </div>
                </div>
                <div class="field-group full-width">
                  <label>Description</label>
                  <textarea
                    v-model="rule.description"
                    placeholder="Enter task description"
                    class="form-input form-textarea"
                    rows="2"
                  ></textarea>
                  <div v-if="currentTrigger === 'MANGO_VOICE'" class="placeholder-hint">
                    <i class="fa fa-info-circle"></i>
                    <span v-pre>Available placeholders: <code>{{callerNumber}}</code> <code>{{callerId}}</code> <code>{{callTime}}</code> <code>{{dialedNumberName}}</code></span>
                  </div>
                  <div v-else-if="currentTrigger && !isExternalTrigger" class="placeholder-hint">
                    <i class="fa fa-info-circle"></i>
                    Patient name, appointment date, and procedure will be automatically added to the task.
                  </div>
                </div>
                <div class="field-row">
                  <div class="field-group">
                    <label>Due Date</label>
                    <multiselect
                      v-model="rule.dueDateObj"
                      :options="dueDateOptions"
                      :searchable="false"
                      :show-labels="false"
                      label="text"
                      track-by="value"
                      placeholder="Select due date..."
                      class="workflow-multiselect"
                      @input="(val) => rule.dueDate = val ? val.value : ''"
                    />
                  </div>
                  <div class="field-group">
                    <label>Attach Wiki</label>
                    <multiselect
                      v-model="rule.wikis"
                      :options="wikisWithoutTemplates"
                      :multiple="true"
                      :searchable="true"
                      :close-on-select="false"
                      :show-labels="false"
                      label="Name"
                      track-by="WikiPageID"
                      placeholder="Select wikis..."
                      class="workflow-multiselect"
                    />
                  </div>
                </div>
                <div class="field-group full-width">
                  <label>Tags</label>
                  <multiselect
                    v-model="rule.tagsList"
                    :options="availableTags"
                    :multiple="true"
                    :taggable="true"
                    :searchable="true"
                    :close-on-select="false"
                    :show-labels="false"
                    tag-placeholder="Add tag"
                    placeholder="Select or add tags..."
                    class="workflow-multiselect"
                    @tag="(newTag) => addNewTag(newTag, rule)"
                  />
                </div>
              </div>
            </div>
            <button class="add-rule-btn-card" @click="addTaskRule">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
              Add Task Rule
            </button>
          </div>

          <!-- Horizontal Table Layout -->
          <div v-else class="task-rules-table-wrapper">
            <table class="task-rules-table">
              <thead>
                <tr>
                  <th v-if="triggerRequiresProcCode" width="12%">If Proc Code Matches...</th>
                  <th :width="triggerRequiresProcCode ? '10%' : '15%'">Assign To</th>
                  <th width="33%">Task Description</th>
                  <th width="16%">Due Date</th>
                  <th width="12%">Attach Wiki?</th>
                  <th width="12%">Tags</th>
                  <th width="5%"></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(rule, index) in selectedNodeData.properties.taskRules || []"
                  :key="index"
                >
                  <td v-if="triggerRequiresProcCode">
                    <multiselect
                      v-model="rule.procCodes"
                      :options="procCodesList"
                      :multiple="true"
                      :searchable="true"
                      :close-on-select="false"
                      :show-labels="false"
                      label="text"
                      track-by="value"
                      placeholder="Select..."
                      class="workflow-multiselect workflow-multiselect-table"
                    />
                  </td>
                  <td>
                    <multiselect
                      v-model="rule.assignees"
                      :options="employeesList"
                      :multiple="true"
                      :searchable="true"
                      :close-on-select="false"
                      :show-labels="false"
                      label="displayName"
                      track-by="EmployeeID"
                      placeholder="Select..."
                      class="workflow-multiselect workflow-multiselect-table"
                    />
                  </td>
                  <td>
                    <textarea
                      v-model="rule.description"
                      placeholder="Enter task description..."
                      class="form-input table-textarea"
                      rows="3"
                    ></textarea>
                    <div v-if="currentTrigger === 'MANGO_VOICE'" class="placeholder-hint">
                      <i class="fa fa-info-circle"></i>
                      <span v-pre>Placeholders: <code>{{callerNumber}}</code> <code>{{callerId}}</code> <code>{{callTime}}</code> <code>{{dialedNumberName}}</code></span>
                    </div>
                    <div v-else-if="currentTrigger && !isExternalTrigger" class="placeholder-hint">
                      <i class="fa fa-info-circle"></i>
                      Patient &amp; appointment info auto-added.
                    </div>
                  </td>
                  <td>
                    <multiselect
                      v-model="rule.dueDateObj"
                      :options="dueDateOptions"
                      :searchable="false"
                      :show-labels="false"
                      label="text"
                      track-by="value"
                      placeholder="Select..."
                      class="workflow-multiselect workflow-multiselect-table workflow-multiselect-wide-dropdown"
                      @input="(val) => rule.dueDate = val ? val.value : ''"
                    />
                  </td>
                  <td>
                    <multiselect
                      v-model="rule.wikis"
                      :options="wikisWithoutTemplates"
                      :multiple="true"
                      :searchable="true"
                      :close-on-select="false"
                      :show-labels="false"
                      label="Name"
                      track-by="WikiPageID"
                      placeholder="Select..."
                      class="workflow-multiselect workflow-multiselect-table"
                    />
                  </td>
                  <td>
                    <multiselect
                      v-model="rule.tagsList"
                      :options="availableTags"
                      :multiple="true"
                      :taggable="true"
                      :searchable="true"
                      :close-on-select="false"
                      :show-labels="false"
                      tag-placeholder="Add tag"
                      placeholder="Select or add..."
                      class="workflow-multiselect workflow-multiselect-table"
                      @tag="(newTag) => addNewTag(newTag, rule)"
                    />
                  </td>
                  <td class="action-cell">
                    <button class="remove-rule-btn" @click="removeTaskRule(index)">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <button class="add-rule-btn mt-3" @click="addTaskRule">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
              Add Rule
            </button>
          </div>
        </div>

        <!-- Wait Settings -->
        <div class="setting-section" v-if="selectedNodeData.type === 'wait'">
          <label>Wait Type</label>
          <div class="radio-group">
            <label class="radio-option">
              <input
                type="radio"
                v-model="selectedNodeData.properties.waitType"
                value="duration"
              >
              <span>Wait for duration</span>
            </label>
            <label class="radio-option">
              <input
                type="radio"
                v-model="selectedNodeData.properties.waitType"
                value="timestamp"
              >
              <span>Wait until specific time</span>
            </label>
          </div>

          <!-- Duration Input -->
          <div v-if="selectedNodeData.properties.waitType === 'duration'" class="mt-3">
            <label>Duration</label>
            <div class="duration-inputs">
              <input
                type="number"
                v-model="selectedNodeData.properties.durationValue"
                min="1"
                class="form-input duration-value"
                placeholder="1"
              >
              <multiselect
                v-model="selectedNodeData.properties.durationUnitObj"
                :options="durationUnitOptions"
                :searchable="false"
                :show-labels="false"
                :allow-empty="false"
                label="text"
                track-by="value"
                placeholder="Select unit..."
                class="workflow-multiselect duration-unit-select"
                @input="(val) => selectedNodeData.properties.durationUnit = val ? val.value : 'hours'"
              />
            </div>
          </div>

          <!-- Timestamp Input -->
          <div v-if="selectedNodeData.properties.waitType === 'timestamp'" class="mt-3">
            <label>Wait Until</label>
            <input
              type="datetime-local"
              v-model="selectedNodeData.properties.waitUntil"
              class="form-input"
            >
          </div>
        </div>

        <!-- Send Form Settings -->
        <div class="setting-section" v-if="selectedNodeData.type === 'sendform'">
          <label>Select Form</label>
          <multiselect
            v-model="selectedNodeData.properties.formObj"
            :options="formsList"
            :searchable="true"
            :show-labels="false"
            label="Name"
            track-by="FormID"
            placeholder="Choose a form..."
            class="workflow-multiselect"
            @input="(val) => selectedNodeData.properties.formId = val ? val.FormID : ''"
          />
          <p v-if="formsList.length === 0" class="help-text text-warning">
            No forms found. Create forms in the Forms module first.
          </p>

          <div class="checkbox-group mt-4">
            <label class="checkbox-option">
              <input
                type="checkbox"
                v-model="selectedNodeData.properties.newPatientOnly"
              >
              <span>Send only to new patients</span>
            </label>
          </div>

          <label class="mt-3">Delivery Method</label>
          <p class="help-text">Select how you want to send the form link to the patient.</p>
          <div class="checkbox-group">
            <label class="checkbox-option">
              <input
                type="checkbox"
                v-model="selectedNodeData.properties.sendViaEmail"
              >
              <span>Send via Email</span>
            </label>
            <label class="checkbox-option">
              <input
                type="checkbox"
                v-model="selectedNodeData.properties.sendViaSms"
              >
              <span>Send via SMS</span>
            </label>
          </div>

          <!-- Email Timing Options -->
          <div v-if="selectedNodeData.properties.sendViaEmail" class="mt-3 delivery-options-card">
            <div class="delivery-method-header">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
              <span>Email Delivery</span>
            </div>
            <label>Send Email When</label>
            <multiselect
              v-model="selectedNodeData.properties.emailSendWhenObj"
              :options="smsSendOptions"
              :searchable="false"
              :show-labels="false"
              label="text"
              track-by="value"
              placeholder="Select when..."
              class="workflow-multiselect workflow-multiselect-wide-dropdown"
              @input="(val) => selectedNodeData.properties.emailSendWhen = val ? val.value : 'immediately'"
            />
          </div>

          <!-- SMS Timing Options -->
          <div v-if="selectedNodeData.properties.sendViaSms" class="mt-3 delivery-options-card">
            <div class="delivery-method-header">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
              <span>SMS Delivery</span>
            </div>
            <label>Send SMS When</label>
            <multiselect
              v-model="selectedNodeData.properties.smsSendWhenObj"
              :options="smsSendOptions"
              :searchable="false"
              :show-labels="false"
              label="text"
              track-by="value"
              placeholder="Select when..."
              class="workflow-multiselect workflow-multiselect-wide-dropdown"
              @input="(val) => selectedNodeData.properties.smsSendWhen = val ? val.value : 'immediately'"
            />
          </div>
        </div>

        <!-- Kanban Settings -->
        <div class="setting-section" v-if="selectedNodeData.type === 'kanban'">
          <label>Proc Codes</label>
          <multiselect
            v-model="selectedNodeData.properties.procCodes"
            :options="procCodesList"
            :multiple="true"
            :searchable="true"
            :close-on-select="false"
            :show-labels="false"
            label="text"
            track-by="value"
            placeholder="Select proc codes..."
            class="workflow-multiselect"
          />
          <p class="help-text mt-1">Optional — limit this action to procedures with these codes. Leave empty and it runs for EVERY procedure this trigger fires on.</p>
          <div
            v-if="!selectedNodeData.properties.procCodes || selectedNodeData.properties.procCodes.length === 0"
            class="proc-codes-empty-hint"
          >
            <i class="fa fa-info-circle"></i>
            Runs for every procedure — add codes to narrow it.
          </div>

          <label class="mt-3">Kanban Board</label>
          <multiselect
            v-model="selectedNodeData.properties.boardObj"
            :options="kanbanBoards"
            :searchable="true"
            :show-labels="false"
            label="name"
            track-by="id"
            placeholder="Select board..."
            class="workflow-multiselect"
            @input="onKanbanBoardSelect"
          />

          <label class="mt-3">Select Column</label>
          <multiselect
            v-model="selectedNodeData.properties.columnObj"
            :options="selectedBoardColumns"
            :searchable="true"
            :show-labels="false"
            label="name"
            track-by="id"
            placeholder="Select column..."
            class="workflow-multiselect"
            @input="(val) => selectedNodeData.properties.columnId = val ? val.id : ''"
          />

          <label class="mt-3">Card Action</label>
          <multiselect
            v-model="selectedNodeData.properties.cardActionObj"
            :options="kanbanCardActions"
            :searchable="false"
            :show-labels="false"
            label="text"
            track-by="value"
            placeholder="Select action..."
            class="workflow-multiselect"
            @input="(val) => selectedNodeData.properties.cardAction = val ? val.value : 'add'"
          />

          <!-- Card Properties for Add/Update actions -->
          <div v-if="selectedNodeData.properties.cardAction !== 'move'" class="mt-3">
            <label>Card Title</label>
            <input
              type="text"
              v-model="selectedNodeData.properties.cardTitle"
              placeholder="Enter card title..."
              class="form-input"
            >

            <label class="mt-3">Card Description</label>
            <textarea
              v-model="selectedNodeData.properties.cardDescription"
              placeholder="Enter card description..."
              rows="3"
              class="form-textarea"
            ></textarea>

            <label class="mt-3">Card Tags</label>
            <input
              type="text"
              v-model="selectedNodeData.properties.cardTags"
              placeholder="tag1, tag2, tag3"
              class="form-input"
            >
          </div>

          <!-- Move to Column for Move action -->
          <div v-if="selectedNodeData.properties.cardAction === 'move'" class="mt-3">
            <label>Move to Column</label>
            <multiselect
              v-model="selectedNodeData.properties.moveToColumnObj"
              :options="selectedBoardColumns"
              :searchable="true"
              :show-labels="false"
              label="name"
              track-by="id"
              placeholder="Select destination column..."
              class="workflow-multiselect"
              @input="(val) => selectedNodeData.properties.moveToColumnId = val ? val.id : ''"
            />
          </div>

          <p class="help-text mt-2">Configure how this workflow interacts with the selected Kanban board.</p>
        </div>

        <!-- Slack Settings -->
        <div class="setting-section" v-if="selectedNodeData.type === 'slack'">
          <!-- Slack Not Connected Notice -->
          <div v-if="!slackConnected" class="feature-notice">
            <div class="notice-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 10c-.83 0-1.5-.67-1.5-1.5v-5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5v5c0 .83-.67 1.5-1.5 1.5z"></path><path d="M20.5 10H19V8.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"></path></svg>
            </div>
            <div class="notice-content">
              <span class="notice-title">Slack Not Connected</span>
              <span class="notice-text">Connect your Slack workspace in Settings to use this action.</span>
            </div>
          </div>

          <div :class="{ 'feature-disabled': !slackConnected }">
            <label>Slack Channel</label>
            <multiselect
              v-model="selectedNodeData.properties.slackChannelObj"
              :options="slackChannels"
              :searchable="true"
              :show-labels="false"
              :custom-label="formatSlackChannel"
              track-by="id"
              placeholder="Select channel..."
              class="workflow-multiselect"
              @input="onSlackChannelSelect"
            />

            <label class="mt-3">Message</label>
            <textarea
              v-model="selectedNodeData.properties.slackMessage"
              :placeholder="slackMessagePlaceholder"
              rows="4"
              class="form-textarea"
            ></textarea>

            <div class="help-text mt-2">
              <strong>Available variables:</strong>
              <code v-pre>{{patient_name}}</code>, <code v-pre>{{patient_phone}}</code>,
              <code v-pre>{{appointment_date}}</code>, <code v-pre>{{appointment_time}}</code>,
              <code v-pre>{{provider_name}}</code>
            </div>
          </div>
        </div>

        <!-- Trigger Settings (for trigger nodes) -->
        <div class="setting-section" v-if="selectedNodeData.type === 'xfollowups'">
          <label>Trigger After X Followups</label>
          <input
            type="number"
            v-model="selectedNodeData.properties.followupCount"
            min="1"
            placeholder="Enter number of followups"
            class="form-input"
          >
          <p class="help-text">The workflow will trigger after this many followups are completed.</p>
        </div>

        <!-- Mango Voice Trigger Settings -->
        <div class="setting-section" v-if="selectedNodeData.type === 'mangovoice'">
          <label>Event Type</label>
          <multiselect
            v-model="selectedNodeData.properties.mangoEventTypeObj"
            :options="mangoEventTypeOptions"
            :searchable="false"
            :show-labels="false"
            label="text"
            track-by="value"
            placeholder="Select event type..."
            class="workflow-multiselect"
            @input="onMangoEventTypeSelect"
          />
          <p class="help-text">Select which Mango Voice event should trigger this workflow.</p>
        </div>
      </div>
      <!-- Action Buttons - Outside scrollable area for proper sticky behavior -->
      <div class="settings-actions">
        <button class="delete-node-btn" @click="deleteSelectedNode">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
          Delete Node
        </button>
        <div class="right-actions">
          <button class="cancel-btn" @click="closeSettings">Cancel</button>
          <button class="save-btn" @click="saveSettings">Save Changes</button>
        </div>
      </div>
    </div>

    <!-- Loading Overlay -->
    <CommonOverlay :show="isLoading" title="Processing" message="Please wait..." />
  </div>
</template>

<script>
/* eslint-disable */

import { mapState, mapActions } from 'vuex';
import ErrorDisplay from "@/components/ErrorDisplay/ErrorDisplay";
import CommonOverlay from "@/components/CommonOverlay";
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import mixin from '../../../mixin.js';
import AwsStlConverter from './AwsStlConverter';
import CommonQueries from '../../../sql/queries';
import { collectWorkflowSmsIssues } from '../libraries/validate-workflow-sms';
import moment from 'moment';
import { listVerifiedEmails } from '@/utils/verifiedSender.js';
import { isEmailEnabled, isSmsEnabled, hasSmsNumber, SMS_DISABLED_MESSAGE } from '@/utils/siteCapability.js';
import { withoutReviewRequestTemplates } from '@/utils/wikiTags';

export default {
  name: 'WorkflowBuilder2',
  components: { ErrorDisplay, CommonOverlay, Multiselect },
  mixins: [mixin],
  props: {
    workflowID: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      activeTab: 'triggers',
      nodes: [],
      connections: [],
      nextNodeId: 1,
      nextConnectionId: 1,
      selectedNode: null,
      draggingNode: null,
      dragOffset: { x: 0, y: 0 },
      showSettings: false,
      selectedNodeData: {},
      connectionInProgress: null,
      mousePosition: { x: 0, y: 0 },
      // Snap-to-connect feature
      snapTarget: null, // Node we're snapping to { nodeId, point: 'top'|'bottom' }
      snapThreshold: 80, // Distance in pixels to trigger snap
      isDraggingFromPalette: false, // True when dragging a new node from left panel
      // Click vs drag detection
      dragStartPos: null, // Initial mouse position on mousedown
      hasDragged: false, // True if mouse moved more than threshold during drag
      dragThreshold: 5, // Pixels of movement to consider it a drag vs click
      isDirty: false,
      isLoading: false,
      canActivate: false,
      taskRulesLayout: 'vertical', // 'vertical' (card) or 'horizontal' (table) — cards are the default: the table's narrow Due Date column clips its dropdown
      workflowData: null, // Loaded workflow data from API
      workflowName: '', // Workflow name for saving
      workflowSaved: false, // Track if workflow has been saved
      loadedForms: [], // Forms loaded from API (stored locally since store doesn't persist)
      existingWorkflows: [], // All existing workflows for trigger validation

      // Triggers - matching original workflow triggers (all triggers from original Workflow)
      triggers: [
        {
          type: 'newpatient',
          label: 'New Patient',
          description: 'Triggers when a new patient is entered into Open Dental',
          icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="8.5" cy="7" r="4"></circle><line x1="20" y1="8" x2="20" y2="14"></line><line x1="23" y1="11" x2="17" y2="11"></line></svg>',
          enabled: false
        },
        {
          type: 'appointmentscheduled',
          label: 'Appointment Scheduled',
          description: 'Triggers when a new appointment is scheduled',
          icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line><path d="M9 16l2 2 4-4"></path></svg>',
          enabled: true
        },
        {
          type: 'appointmentbroken',
          label: 'Appointment Broken',
          description: 'Triggers when an appointment is cancelled',
          icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line><line x1="9" y1="14" x2="15" y2="20"></line><line x1="15" y1="14" x2="9" y2="20"></line></svg>',
          enabled: true
        },
        {
          type: 'appointmentcompleted',
          label: 'Appointment Completed',
          description: 'Triggers when an appointment is marked completed',
          icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line><polyline points="9 16 12 19 16 14"></polyline></svg>',
          enabled: true
        },
        {
          type: 'procaddedquickadd',
          label: 'Procedure added via Procs Quick Add',
          description: 'Triggers when a procedure is added via Account > Procedures > Quick Add in Open Dental',
          icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="12" y1="12" x2="12" y2="18"></line><line x1="9" y1="15" x2="15" y2="15"></line></svg>',
          enabled: true
        },
        {
          type: 'preauthreceived',
          label: 'Pre-Auth Marked As Received',
          description: 'Triggers when a Pre-Auth claim is marked as Received in Open Dental',
          icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><path d="M9 15l2 2 4-4"></path></svg>',
          enabled: false
        },
        {
          type: 'xfollowups',
          label: '# Teamio Followups Completed',
          description: 'Triggers when an X number of followups for a patient completed',
          icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 11 12 14 22 4"></polyline><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></svg>',
          enabled: false
        },
        {
          type: 'proceduremarkedreferral',
          label: 'A Procedure Is Marked As Referral',
          description: 'Triggers when a code is marked as Referral in Open Dental',
          icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="8.5" cy="7" r="4"></circle><path d="M17 11l2 2 4-4"></path></svg>',
          enabled: false
        },
        {
          type: 'websiteform',
          label: 'New Website Form Submission',
          description: 'Triggers when website form is submitted',
          icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line></svg>',
          enabled: false
        },
        {
          type: 'facebooklead',
          label: 'Facebook Lead Generated',
          description: 'Triggers when a new Facebook Lead is Generated',
          icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>',
          enabled: false
        },
        {
          type: 'googlelead',
          label: 'Google Lead Generated',
          description: 'Triggers when a new Google Lead is Generated',
          icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M12 8v8"></path><path d="M8 12h8"></path></svg>',
          enabled: false
        },
        {
          type: 'mangovoice',
          label: 'Mango Voice Event',
          description: 'Triggers on Mango Voice phone events (e.g., missed calls)',
          icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>',
          enabled: true
        }
      ],

      // Actions - matching original workflow actions with enabled/disabled states
      actions: [
        {
          type: 'sms',
          label: 'Send Text Message',
          description: 'Send a text message to a contact',
          icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>',
          enabled: true
        },
        {
          type: 'email',
          label: 'Send Email',
          description: 'Send email to a contact',
          icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>',
          enabled: true
        },
        {
          type: 'assigntask',
          label: 'Assign Teamio Task',
          description: 'Assign task to someone',
          icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect><path d="M9 14l2 2 4-4"></path></svg>',
          enabled: true
        },
        {
          type: 'popup',
          label: 'Create an Open Dental Popup',
          description: 'Create a popup window in Open Dental',
          icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="9" y1="9" x2="15" y2="15"></line><line x1="15" y1="9" x2="9" y2="15"></line></svg>',
          enabled: false
        },
        {
          type: 'mailletter',
          label: 'Mail Letter',
          description: 'Mail a Letter to patient',
          icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 8V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-2"></path><rect x="18" y="8" width="4" height="8" rx="1"></rect></svg>',
          enabled: false
        },
        {
          type: 'wait',
          label: 'Wait',
          description: 'Wait for a certain duration',
          icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>',
          enabled: false
        },
        {
          type: 'slack',
          label: 'Send Slack Message',
          description: 'Send a message to a Slack channel',
          icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 10c-.83 0-1.5-.67-1.5-1.5v-5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5v5c0 .83-.67 1.5-1.5 1.5z"></path><path d="M20.5 10H19V8.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"></path><path d="M9.5 14c.83 0 1.5.67 1.5 1.5v5c0 .83-.67 1.5-1.5 1.5S8 21.33 8 20.5v-5c0-.83.67-1.5 1.5-1.5z"></path><path d="M3.5 14H5v1.5c0 .83-.67 1.5-1.5 1.5S2 16.33 2 15.5 2.67 14 3.5 14z"></path></svg>',
          enabled: false
        },
        {
          type: 'prospectivelead',
          label: 'Create a Follow-up for Prospective Lead',
          description: 'Create a Follow-up for Prospective Lead',
          icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>',
          enabled: false
        },
        {
          type: 'sendform',
          label: 'Send a Form',
          description: 'Send a Form to the patient to digitally sign',
          icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="12" y1="18" x2="12" y2="12"></line><line x1="9" y1="15" x2="15" y2="15"></line></svg>',
          enabled: false
        },
        {
          type: 'kanban',
          label: 'Kanban',
          description: 'Take an action related to Kanban board',
          icon: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="9" y1="3" x2="9" y2="21"></line><line x1="15" y1="3" x2="15" y2="21"></line></svg>',
          // Enabled 2026-08-17: the KANBAN consumer was rebuilt against the real board
          // model (teamio-utility lib/kanban-action.js) — the action actually works now.
          enabled: true
        }
      ]
    };
  },

  computed: {
    ...mapState({
      siteID: state => state.customer.siteID,
      employees: state => state.customer.employees,
      wikiPages: state => state.wiki.wikipages, // Note: lowercase 'wikipages' in store state
      settings: state => state.customer.settings, // Raw settings array
      forms: state => state.forms.forms,
      kanban: state => state.kanban,
      userID: state => state.customer.empid,
      procCodes: state => state.customer.procCodes, // Procedure codes from customer store
      providers: state => state.customer.providers, // Providers from customer store (for new patient provider filter)
      slackStatus: state => state.slack.slackStatus // Slack connection status from slack store
    }),

    // The site's real IANA timezone, read from the GENERAL_TIMEZONE settings row.
    //
    // This used to be mapState'd from `state.customer.timezone` — a property that does not exist
    // on the customer store, so it was always undefined and the `|| 'America/New_York'` fallback
    // below always won. That literal got baked into every workflow's Step Functions JSON at save
    // time, on every site, and the backend scheduler then sent delayed workflow messages at 8am
    // EASTERN everywhere. Smile Bar (Mountain) was texting patients at 6:00 AM. Fixed 2026-08-12.
    //
    // The backend (teamio-utility scheduleIt) now resolves GENERAL_TIMEZONE from the DB itself and
    // that value takes precedence, which is what actually fixes existing workflows — they'd
    // otherwise keep the stale zone baked in until someone re-saved them. This is kept accurate
    // so the value we ship stops being a lie and the backend's fallback is a sane one.
    siteTimezone() {
      return (this.settings || []).find(s => s.Property === 'GENERAL_TIMEZONE')?.PropertyValue
        || 'America/New_York';
    },

    isEditMode() {
      return !!(this.workflowID || this.$route?.params?.workflowID);
    },

    // Get the effective workflow ID (from prop or route)
    effectiveWorkflowID() {
      return this.workflowID || this.$route?.params?.workflowID || null;
    },

    // Filtered triggers and actions based on enabled state.
    // The Mango Voice trigger is additionally gated on the site having Mango Voice
    // set up (a MANGO_VOICE_SECURITY_TOKEN in settings) — otherwise a workflow built
    // on it can never fire (the webhook 403s without the token). Same shape as the
    // send-email/send-text feature gating below. (Tulip Tree, 2026-07-18.)
    enabledTriggers() {
      return this.triggers.filter(t => t.enabled && (t.type !== 'mangovoice' || this.mangoVoiceEnabled));
    },
    disabledTriggers() {
      return this.triggers.filter(t => !(t.enabled && (t.type !== 'mangovoice' || this.mangoVoiceEnabled)));
    },
    enabledActions() {
      return this.actions.filter(a => a.enabled);
    },
    disabledActions() {
      return this.actions.filter(a => !a.enabled);
    },

    // Feature flags based on customer settings
    // Note: this.allSettings comes from the mixin (transforms settings array to keyed object)
    emailFeatureEnabled() {
      // Derived email capability via the shared five-shape parser — identical to the
      // old naive `.trim() !== ''` for bare-string prod values, but correctly reports
      // disabled for senderless shapes like "[]"/"{}". See src/utils/siteCapability.js.
      return isEmailEnabled(this.allSettings);
    },

    // The SMS step is usable only where an SMS may be SENT (siteCapability.isSmsEnabled) —
    // a WhatsApp-only office has a number but outbound SMS is off, so its notice explains
    // that instead of offering to set texting up.
    smsFeatureEnabled() {
      return isSmsEnabled(this.allSettings);
    },
    smsOutboundOff() {
      return hasSmsNumber(this.allSettings) && !isSmsEnabled(this.allSettings);
    },
    smsDisabledMessage() {
      return SMS_DISABLED_MESSAGE;
    },

    // Mango Voice ("Missed Call") is only usable as a trigger once the site has a
    // MANGO_VOICE_SECURITY_TOKEN configured (Settings ▸ Integrations). Without it the
    // mango-voice webhook rejects every call, so a workflow built on this trigger would
    // never run. Gate the trigger on the token, same as email/SMS above.
    mangoVoiceEnabled() {
      if (!this.allSettings) return false;
      const token = this.allSettings.MANGO_VOICE_SECURITY_TOKEN;
      return token &&
             token.PropertyValue &&
             token.PropertyValue.trim() !== '';
    },

    slackConnected() {
      // Use slack store status (populated by getSlackStatus API call)
      return this.slackStatus && this.slackStatus.connected === true;
    },

    // Data lists for dropdowns.
    // Shape handling lives in @/utils/verifiedSender.js — five storage shapes,
    // because main-service writes a bare address while teamio-admin writes a CSV
    // list. The hand-rolled split here handled only two of them.
    verifiedEmails() {
      return listVerifiedEmails(this.allSettings && this.allSettings.GENERAL_VERIFIED_EMAILS);
    },

    // First verified email — used as the read-only "From Email Address" display in
    // the Send Email node (2026-05-17). Mirrors the backend's selection logic in
    // utility.js sendEmailWithSendGrid: take the first entry from GENERAL_VERIFIED_EMAILS.
    // If empty, the UI shows "Not configured — contact admin" and the backend skips the
    // send with a workflow output entry (no DLQ).
    firstVerifiedEmail() {
      return (this.verifiedEmails && this.verifiedEmails[0]) || '';
    },

    // Site name for read-only "From Name" display. Looked up from the customer
    // store's sites array using the current siteID. Falls back to empty string if
    // the site record isn't loaded yet.
    currentSiteName() {
      const sites = this.$store.state.customer.sites || [];
      const site = sites.find(s => s.SiteID === this.siteID);
      return (site && site.Name) || '';
    },

    wikiTemplates() {
      if (!this.wikiPages) return [];
      // Filter wiki pages that are email/sms templates (used for email/SMS rules)
      return this.wikiPages.filter(wp =>
        wp.Tags && (wp.Tags.includes('email-template') || wp.Tags.includes('sms-template'))
      );
    },

    // Filtered email-only templates (for general email rules). Also excludes reminder-specific
    // templates (apt-reminder / recall-reminder) — those are owned by the reminder features and
    // must not be selectable in a general workflow Send Email action — and Reviews templates,
    // whose {{review_link}} only the Reviews send path can fill.
    emailTemplates() {
      if (!this.wikiPages) return [];
      return withoutReviewRequestTemplates(this.wikiPages).filter(wp =>
        wp.Tags && wp.Tags.includes('email-template') &&
        !wp.Tags.includes('apt-reminder') && !wp.Tags.includes('recall-reminder')
      );
    },

    // Filtered SMS-only templates (for general SMS rules). Also excludes reminder-specific
    // templates (apt-reminder / recall-reminder) — those are owned by the reminder features and
    // must not be selectable in a general workflow Send Text Message action — and Reviews
    // templates, whose {{review_link}} only the Reviews send path can fill.
    smsTemplates() {
      if (!this.wikiPages) return [];
      return withoutReviewRequestTemplates(this.wikiPages).filter(wp =>
        wp.Tags && wp.Tags.includes('sms-template') &&
        !wp.Tags.includes('apt-reminder') && !wp.Tags.includes('recall-reminder')
      );
    },

    // Wiki pages that are NOT templates - used for task descriptions
    wikisWithoutTemplates() {
      if (!this.wikiPages) return [];
      // Get wiki pages that are not email/sms templates
      return this.wikiPages.filter(wp =>
        !wp.Tags || (!wp.Tags.includes('email-template') && !wp.Tags.includes('sms-template'))
      );
    },

    // Get current trigger type from nodes
    currentTrigger() {
      const triggerNode = this.nodes.find(n => this.isTriggerNode(n.type));
      if (!triggerNode) return null;
      return this.mapTypeToBlockType(triggerNode.type);
    },

    // Check if current trigger requires procedure codes (appointment-based triggers)
    // External triggers like MANGO_VOICE don't have proc codes
    triggerRequiresProcCode() {
      const externalTriggers = ['MANGO_VOICE'];
      return !externalTriggers.includes(this.currentTrigger);
    },

    // Check if current trigger is an external trigger (non-appointment based)
    isExternalTrigger() {
      const externalTriggers = ['MANGO_VOICE'];
      return externalTriggers.includes(this.currentTrigger);
    },

    employeesList() {
      if (!this.employees || !Array.isArray(this.employees)) return [];

      // Filter for active employees only (matching Workflow v1 behavior)
      const activeEmployees = this.employees.filter(emp => emp.Active === 1);

      // Format employees for dropdown - use Name or FirstName/LastName
      const formattedEmployees = activeEmployees.map(emp => ({
        ...emp,
        displayName: emp.Name || `${emp.FirstName || ''} ${emp.LastName || ''}`.trim()
      }));

      // Add "Whoever marked Apt Complete" option for Appointment Completed trigger
      if (this.currentTrigger === 'APPOINTMENT_COMPLETED') {
        return [
          {
            EmployeeID: 'Whoever marked Apt Complete',
            displayName: 'Whoever marked Apt Complete',
            Name: 'Whoever marked Apt Complete',
            isSpecialOption: true
          },
          {
            EmployeeID: 'Provider who completed the procedure',
            displayName: 'Provider who completed the procedure',
            Name: 'Provider who completed the procedure',
            isSpecialOption: true
          },
          ...formattedEmployees
        ];
      }

      return formattedEmployees;
    },

    // Formatted proc codes for dropdown
    // Uses CodeNum as value (stored as string in workflow data)
    procCodesList() {
      if (!this.procCodes || !Array.isArray(this.procCodes)) return [];
      return this.procCodes.map(code => ({
        value: String(code.CodeNum || code.ProcCode), // Ensure string for matching
        text: `${code.ProcCode} - ${code.Descript || ''}`.trim()
      }));
    },

    // Providers formatted for multiselect dropdowns (used in new patient provider filter)
    providersList() {
      if (!this.providers || !Array.isArray(this.providers)) return [];
      return this.providers
        .filter(p => !p.IsHidden)
        .map(p => ({
          ProvNum: p.ProvNum,
          displayName: `${p.FName} ${p.LName}`.trim() || p.Abbr
        }));
    },

    // Due date options for task rules (matching original workflow)
    // Includes both "after" and "before" variants for backwards compatibility
    //
    // ⚠️ KEEP IN SYNC with teamio-utility `computeTaskDueDate()` (utility-utils.js).
    // That switch resolves each `value` below into the actual due date, and its
    // `default:` branch is "due today" with NO error and NO log — so a value offered
    // here that the backend doesn't know silently creates every task due immediately.
    // Deploy the backend FIRST, then the frontend. Values are shared across triggers
    // (only the label changes), so a rule keeps its meaning if the trigger is swapped.
    dueDateOptions() {
      // Make due date options trigger-aware (match v1 behavior)
      const trigger = this.currentTrigger;

      switch (trigger) {
        case 'PROCEDURE_ADDED_VIA_PROCS_QUICK_ADD':
          // For added procedures (no appointment), use "after procedure is added"
          return [
            { value: 'immediately', text: 'Immediately' },
            { value: '1day', text: '1 Day After Procedure is Added' },
            { value: '2days', text: '2 Days After Procedure is Added' },
            { value: '1week', text: '1 Week After Procedure is Added' }
          ];

        case 'APPOINTMENT_COMPLETED':
          // For completed appointments, use "after appointment is completed"
          return [
            { value: 'immediately', text: 'Immediately' },
            { value: '1day', text: '1 Day After Appointment is completed' },
            { value: '2days', text: '2 Days After Appointment is completed' },
            { value: '1week', text: '1 Week After Appointment is completed' },
            { value: '2weeks', text: '2 Weeks After Appointment is completed' },
            { value: '4weeks', text: '4 Weeks After Appointment is completed' },
            { value: '6weeks', text: '6 Weeks After Appointment is completed' }
          ];

        case 'APPOINTMENT_BROKEN':
          // For broken appointments, use "after appointment is broken"
          return [
            { value: 'immediately', text: 'Immediately' },
            { value: '1day', text: '1 Day After Appointment is Broken' },
            { value: '2days', text: '2 Days After Appointment is Broken' },
            { value: '1week', text: '1 Week After Appointment is Broken' },
            { value: '2weeks', text: '2 Weeks After Appointment is Broken' },
            { value: '4weeks', text: '4 Weeks After Appointment is Broken' },
            { value: '6weeks', text: '6 Weeks After Appointment is Broken' }
          ];

        case 'APPOINTMENT_SCHEDULED':
          // For scheduled appointments, use "before/on appointment date"
          return [
            { value: 'immediately', text: 'Immediately' },
            { value: 'odoa', text: 'On the Day of Appointment' },
            { value: '1dayb', text: '1 Day Before the Appointment Date' },
            { value: '2daysb', text: '2 Days Before the Appointment Date' },
            { value: '1weekb', text: '1 Week Before the Appointment Date' },
            { value: '2weeksb', text: '2 Weeks Before the Appointment Date' },
            { value: '4weeksb', text: '4 Weeks Before the Appointment Date' },
            { value: '6weeksb', text: '6 Weeks Before the Appointment Date' }
          ];

        case 'MANGO_VOICE':
          // For external triggers (no appointment data), use generic time-based options
          return [
            { value: 'immediately', text: 'Immediately' },
            { value: '1day', text: '1 Day from Now' },
            { value: '2days', text: '2 Days from Now' },
            { value: '1week', text: '1 Week from Now' }
          ];

        default:
          // For all other triggers, use generic "after appointment is created"
          return [
            { value: 'immediately', text: 'Immediately' },
            { value: '1day', text: '1 Day After Appointment is created' },
            { value: '2days', text: '2 Days After Appointment is created' },
            { value: '1week', text: '1 Week After Appointment is created' },
            { value: '1dayb', text: '1 Day Before Appointment Date' },
            { value: '2daysb', text: '2 Days Before Appointment Date' },
            { value: '1weekb', text: '1 Week Before Appointment Date' }
          ];
      }
    },

    formsList() {
      // Use locally stored forms (loaded in loadRequiredData)
      return this.loadedForms || [];
    },

    // Available tags for task rules (collect from existing rules + common tags)
    availableTags() {
      const tagsSet = new Set();

      // Add tags from Firestore (persisted site-wide tags)
      if (this.$store.state.tasks.siteTaskTags && Array.isArray(this.$store.state.tasks.siteTaskTags)) {
        this.$store.state.tasks.siteTaskTags.forEach(tag => tagsSet.add(tag));
      }

      // Collect tags from all existing task rules in this workflow
      if (this.selectedNodeData && this.selectedNodeData.properties && this.selectedNodeData.properties.taskRules) {
        this.selectedNodeData.properties.taskRules.forEach(rule => {
          if (rule.tagsList && Array.isArray(rule.tagsList)) {
            rule.tagsList.forEach(tag => tagsSet.add(tag));
          }
          // Also check legacy tags string
          if (rule.tags && typeof rule.tags === 'string') {
            rule.tags.split(',').map(t => t.trim()).filter(t => t).forEach(tag => tagsSet.add(tag));
          }
        });
      }
      return Array.from(tagsSet).sort();
    },

    kanbanBoards() {
      if (!this.kanban || !this.kanban.boards) return [];
      return this.kanban.boards;
    },

    slackChannels() {
      // Get channels from slack store (populated by getChannels API call)
      return this.$store.state.slack.channels || [];
    },

    slackMessagePlaceholder() {
      return 'Enter your message. You can use variables like {patient_name}, {appointment_date}';
    },

    // Get columns for the selected Kanban board
    selectedBoardColumns() {
      if (!this.selectedNodeData || !this.selectedNodeData.properties) return [];
      return this.mapBoardColumns(this.selectedNodeData.properties.boardObj);
    },

    // Kanban card actions
    kanbanCardActions() {
      return [
        { value: 'add', text: 'Add New Card' },
        { value: 'update', text: 'Update Existing Card' },
        { value: 'move', text: 'Move Card to Column' }
      ];
    },

    // Duration unit options for Wait action
    durationUnitOptions() {
      return [
        { value: 'seconds', text: 'Seconds' },
        { value: 'minutes', text: 'Minutes' },
        { value: 'hours', text: 'Hours' },
        { value: 'days', text: 'Days' }
      ];
    },

    // SMS Send When options - trigger-aware (matching dueDateOptions pattern)
    smsSendOptions() {
      const trigger = this.currentTrigger;

      switch (trigger) {
        case 'PROCEDURE_ADDED_VIA_PROCS_QUICK_ADD':
          // For added procedures (no appointment), use "after procedure is added"
          return [
            { value: 'immediately', text: 'Immediately' },
            { value: '1day', text: '1 Day After Procedure is Added' },
            { value: '2days', text: '2 Days After Procedure is Added' },
            { value: '1week', text: '1 Week After Procedure is Added' }
          ];

        case 'APPOINTMENT_COMPLETED':
          // For completed appointments, use "after appointment is completed"
          return [
            { value: 'immediately', text: 'Immediately' },
            { value: '1day', text: '1 Day After Appointment is completed' },
            { value: '2days', text: '2 Days After Appointment is completed' },
            { value: '1week', text: '1 Week After Appointment is completed' }
          ];

        case 'APPOINTMENT_BROKEN':
          // For broken appointments, use "after appointment is broken"
          return [
            { value: 'immediately', text: 'Immediately' },
            { value: '1day', text: '1 Day After Appointment is Broken' },
            { value: '2days', text: '2 Days After Appointment is Broken' },
            { value: '1week', text: '1 Week After Appointment is Broken' }
          ];

        case 'APPOINTMENT_SCHEDULED':
          // For scheduled appointments, use "before/on appointment date"
          return [
            { value: 'immediately', text: 'Immediately' },
            { value: 'odoa', text: 'On the Day of Appointment' },
            { value: '1dayb', text: '1 Day Before Appointment' },
            { value: '2daysb', text: '2 Days Before Appointment' },
            { value: '3daysb', text: '3 Days Before Appointment' },
            { value: '1weekb', text: '1 Week Before Appointment' }
          ];

        default:
          // For all other triggers, use generic options
          return [
            { value: 'immediately', text: 'Immediately' },
            { value: '1dayb', text: '1 Day Before Appointment' },
            { value: '2daysb', text: '2 Days Before Appointment' },
            { value: '3daysb', text: '3 Days Before Appointment' },
            { value: '1weekb', text: '1 Week Before Appointment' }
          ];
      }
    },

    // Mango Voice Event Type options
    mangoEventTypeOptions() {
      return [
        { value: 'missed_call', text: 'Missed Call' }
        // Future options can be added here:
        // { value: 'voicemail', text: 'Voicemail' },
        // { value: 'call_ended', text: 'Call Ended' }
      ];
    },

    // Email Send When options - trigger-aware (same logic as SMS)
    emailSendOptions() {
      const trigger = this.currentTrigger;

      switch (trigger) {
        case 'PROCEDURE_ADDED_VIA_PROCS_QUICK_ADD':
          // For added procedures (no appointment), use "after procedure is added"
          return [
            { value: 'immediately', text: 'Immediately' },
            { value: '1day', text: '1 Day After Procedure is Added' },
            { value: '2days', text: '2 Days After Procedure is Added' },
            { value: '1week', text: '1 Week After Procedure is Added' }
          ];

        case 'APPOINTMENT_COMPLETED':
          // For completed appointments, use "after appointment is completed"
          return [
            { value: 'immediately', text: 'Immediately' },
            { value: '1day', text: '1 Day After Appointment is completed' },
            { value: '2days', text: '2 Days After Appointment is completed' },
            { value: '1week', text: '1 Week After Appointment is completed' }
          ];

        case 'APPOINTMENT_BROKEN':
          // For broken appointments, use "after appointment is broken"
          return [
            { value: 'immediately', text: 'Immediately' },
            { value: '1day', text: '1 Day After Appointment is Broken' },
            { value: '2days', text: '2 Days After Appointment is Broken' },
            { value: '1week', text: '1 Week After Appointment is Broken' }
          ];

        case 'APPOINTMENT_SCHEDULED':
          // For scheduled appointments, use "before/on appointment date"
          return [
            { value: 'immediately', text: 'Immediately' },
            { value: 'odoa', text: 'On the Day of Appointment' },
            { value: '1dayb', text: '1 Day Before Appointment' },
            { value: '2daysb', text: '2 Days Before Appointment' },
            { value: '3daysb', text: '3 Days Before Appointment' },
            { value: '1weekb', text: '1 Week Before Appointment' }
          ];

        default:
          // For all other triggers, use generic options
          return [
            { value: 'immediately', text: 'Immediately' },
            { value: '1dayb', text: '1 Day Before Appointment' },
            { value: '2daysb', text: '2 Days Before Appointment' },
            { value: '3daysb', text: '3 Days Before Appointment' },
            { value: '1weekb', text: '1 Week Before Appointment' }
          ];
      }
    }
  },

  created() {
    // Load required data for settings panels
    this.loadRequiredData();

    // Load workflow if in edit mode (check both prop and route param)
    if (this.workflowID || this.$route?.params?.workflowID) {
      this.loadWorkflow();
    } else if (this.$route?.query?.template) {
      // Initialize from template if creating from template
      this.initializeFromTemplate();
    }
  },

  mounted() {
    document.addEventListener('mousemove', this.updateTempConnection);
    document.addEventListener('mouseup', this.finishConnection);
    // Add beforeunload listener to warn about unsaved changes
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  },

  beforeDestroy() {
    document.removeEventListener('mousemove', this.updateTempConnection);
    document.removeEventListener('mouseup', this.finishConnection);
    // Remove beforeunload listener
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    // Re-enable body scroll if settings panel was open
    document.body.style.overflow = '';
    // Unsubscribe from task tags listener
    this.unsubscribeSiteTaskTags();
  },

  // Vue Router navigation guard for in-app navigation
  beforeRouteLeave(_to, _from, next) {
    if (this.isDirty) {
      const answer = window.confirm('You have unsaved changes. Are you sure you want to leave?');
      if (answer) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },

  methods: {
    ...mapActions('workflow', ['createWorkflow', 'updateWorkflow', 'enableWorkflow', 'getAllWorkflows']),
    ...mapActions('wiki', ['getWikiPages']),
    ...mapActions('forms', ['getForms']),
    ...mapActions('kanban', ['fetchBoards']),
    ...mapActions('customer', ['getProcCodes', 'getEmployees']),
    ...mapActions('slack', ['getSlackStatus', 'getChannels']),
    ...mapActions('tasks', ['subscribeSiteTaskTags', 'unsubscribeSiteTaskTags']),

    // ========================
    // Browser Navigation Guard
    // ========================

    handleBeforeUnload(e) {
      if (this.isDirty) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    },

    // ========================
    // API Integration Methods
    // ========================

    async loadRequiredData() {
      try {
        if (!this.siteID) return;

        // Subscribe to site task tags for tag autocomplete
        // This sets up a real-time listener for tags
        this.subscribeSiteTaskTags({ siteID: this.siteID });

        // Load all required data in parallel for better performance
        const promises = [
          // Load wiki pages for template selection
          this.getWikiPages({ siteID: this.siteID }),
          // Load forms for send form action - store result locally since vuex doesn't persist
          // Note: getForms returns { forms: [...], categories: [...] }
          this.getForms({ siteID: this.siteID }).then(res => {
            this.loadedForms = (res && res.forms) ? res.forms : (Array.isArray(res) ? res : []);
          }),
          // Load procedure codes for task rules
          this.getProcCodes({ siteID: this.siteID })
        ];

        // Load employees if not already loaded (employees usually loaded by Header)
        if (!this.employees || this.employees.length === 0) {
          promises.push(
            this.getEmployees({ sqlQuery: CommonQueries.getEmployees(this.siteID) })
          );
        }

        // Load kanban boards (may not be available for all accounts)
        if (this.fetchBoards) {
          promises.push(
            this.fetchBoards({ siteID: this.siteID }).catch(e => {
              console.log('Kanban boards not available:', e);
            })
          );
        }

        // Load Slack connection status and channels if connected
        promises.push(
          this.getSlackStatus({ siteID: this.siteID }).then(status => {
            // If Slack is connected, also load channels
            if (status && status.connected) {
              return this.getChannels({ siteID: this.siteID }).catch(e => {
                console.log('Error loading Slack channels:', e);
              });
            }
          }).catch(e => {
            console.log('Slack status not available:', e);
          })
        );

        // Load existing workflows to check for duplicate triggers
        promises.push(
          this.getAllWorkflows({ siteID: this.siteID }).then(res => {
            this.existingWorkflows = res || [];
          }).catch(e => {
            console.log('Error loading existing workflows:', e);
            this.existingWorkflows = [];
          })
        );

        await Promise.all(promises);
      } catch (err) {
        console.error('Error loading required data:', err);
      }
    },

    /**
     * Initialize workflow from a quick-start template
     * Creates trigger and action nodes with a connection between them
     */
    initializeFromTemplate() {
      const query = this.$route?.query;
      if (!query?.template || !query?.trigger || !query?.action) return;

      const triggerType = query.trigger;
      const actionType = query.action;
      const workflowName = query.name || '';

      // Validate trigger type exists
      const triggerDef = this.triggers.find(t => t.type === triggerType);
      if (!triggerDef) {
        console.error('Invalid trigger type from template:', triggerType);
        return;
      }

      // Validate action type exists
      const actionDef = this.actions.find(a => a.type === actionType);
      if (!actionDef) {
        console.error('Invalid action type from template:', actionType);
        return;
      }

      // Set workflow name from template
      if (workflowName) {
        this.workflowName = workflowName;
      }

      // Position nodes: trigger at top center, action below
      const triggerX = 300;
      const triggerY = 80;
      const actionX = 300;
      const actionY = 220;

      // Create trigger node
      const triggerId = this.nextNodeId++;
      const triggerNode = {
        id: triggerId,
        type: triggerType,
        x: triggerX,
        y: triggerY,
        properties: {}
      };
      this.nodes.push(triggerNode);

      // Create action node
      const actionId = this.nextNodeId++;
      const actionNode = {
        id: actionId,
        type: actionType,
        x: actionX,
        y: actionY,
        properties: {}
      };
      this.nodes.push(actionNode);

      // Create connection from trigger to action
      this.connections.push({
        id: `c${this.nextConnectionId++}`,
        source: triggerId,
        target: actionId,
        sourcePoint: 'bottom',
        targetPoint: 'top'
      });

      // Mark as dirty since we've created nodes
      this.setDirty();

      // Show toast to guide user
      this.$nextTick(() => {
        this.$toast.success(`Template loaded! Configure the ${actionDef.label} action and save your workflow.`);
      });
    },

    async loadWorkflow() {
      const workflowID = this.effectiveWorkflowID;
      if (!workflowID) return;

      this.isLoading = true;
      try {
        // Use getAllWorkflows and filter by ID (same approach as old workflow builder)
        // This avoids CORS issues with the /get-workflow endpoint
        let workflows = this.existingWorkflows;

        // If existingWorkflows not loaded yet, fetch them
        if (!workflows || workflows.length === 0) {
          workflows = await this.getAllWorkflows({ siteID: this.siteID });
          this.existingWorkflows = workflows || [];
        }

        // Find the workflow by ID (compare as strings to handle type mismatch)
        const workflow = workflows.find(w => String(w.WorkflowID) === String(workflowID));

        if (workflow) {
          this.workflowData = workflow;
          this.workflowName = workflow.Name || '';

          // Convert stored FlowyText blocks to nodes format
          if (workflow.FlowyText) {
            try {
              const flowyData = typeof workflow.FlowyText === 'string'
                ? JSON.parse(workflow.FlowyText)
                : workflow.FlowyText;

              this.loadNodesFromFlowyData(flowyData);
            } catch (parseErr) {
              console.error('Error parsing FlowyText:', parseErr);
            }
          }

          // Only show activate button if workflow is NOT already active
          // Active field is 1 for active, 0 for inactive (or could be boolean)
          this.canActivate = !workflow.Active;
          this.isDirty = false;
        } else {
          this.$toast.error('Workflow not found! Please try again');
        }
      } catch (err) {
        console.error('Error loading workflow:', err);
        this.$toast.error('Error loading workflow. Please try again.');
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Convert FlowyText data back to Workflow2 nodes format
     * Old workflow stores data as array with index positions:
     * - data[0]: blockelemid (name)
     * - data[1]: blockelemid (id number)
     * - data[2]: blockelemtype (the trigger/action type like "Appointment Completed")
     * - attr[]: array of objects like {'data-send-email-properties': '...json...'}
     */
    loadNodesFromFlowyData(flowyData) {
      const blocks = flowyData.blocks || flowyData.blockarr || [];

      if (blocks.length === 0) {
        return;
      }

      // Track ID mapping: old block.id → new unique nodeId
      // This is needed because old workflow may have IDs starting from 0 or duplicates
      // We always assign IDs starting from 1 to avoid Vue key issues with 0
      const idMap = new Map();
      let maxId = 0;

      // First pass: assign unique sequential IDs to all blocks (starting from 1)
      blocks.forEach((block, index) => {
        const oldId = block.id;
        const newId = index + 1; // Always use 1-based indexing for Vue keys

        idMap.set(oldId, newId);
        if (newId > maxId) maxId = newId;
      });

      // Convert each block to a node
      this.nodes = blocks.map((block, index) => {
        const nodeId = index + 1; // Use simple 1-based indexing

        // Extract type from data attributes
        // Old workflow format:
        //   - data has: blockelemtype="triggers"/"actions", blockelemname="Appointment Broken", etc.
        //   - attr has: data-blocktype="assigntask" (the actual node type for actions)
        // Workflow2 format:
        //   - data has: blockelemtype="Appointment Completed" (the actual type name)
        const dataAttrs = block.data || [];
        const attrArray = block.attr || [];
        let blockTypeValue = 'unknown';

        // Build attr map for easier lookup
        const attrMap = {};
        if (Array.isArray(attrArray)) {
          attrArray.forEach(attrObj => {
            if (attrObj && typeof attrObj === 'object') {
              Object.entries(attrObj).forEach(([key, value]) => {
                attrMap[key] = value;
              });
            }
          });
        }

        // Strategy 1: Check data-blocktype in attr (old workflow format for actions)
        if (attrMap['data-blocktype']) {
          blockTypeValue = attrMap['data-blocktype'];
        }

        // Strategy 2: Check blockelemname in data (old workflow format - has human readable name)
        if (blockTypeValue === 'unknown' || blockTypeValue === 'triggers' || blockTypeValue === 'actions') {
          const nameAttr = dataAttrs.find(d => d && d.name === 'blockelemname');
          if (nameAttr && nameAttr.value) {
            blockTypeValue = nameAttr.value;
          }
        }

        // Strategy 3: Check blockelemtype in data (Workflow2 format - has type name directly)
        if (blockTypeValue === 'unknown' || blockTypeValue === 'triggers' || blockTypeValue === 'actions') {
          const typeAttr = dataAttrs.find(d => d && d.name === 'blockelemtype');
          if (typeAttr && typeAttr.value && typeAttr.value !== 'triggers' && typeAttr.value !== 'actions') {
            blockTypeValue = typeAttr.value;
          }
        }

        const nodeType = this.mapBlockTypeToNodeType(blockTypeValue);

        // Build properties from block.attr (where the actual properties like data-send-email-properties are stored)
        const properties = this.extractPropertiesFromData(attrArray, nodeType, dataAttrs);

        // Extract position from the block's style attribute
        // The attr array contains: {style: 'left: 455.741px; top: 66.8519px;'}
        let x = 300 + (index * 50); // Default fallback
        let y = 100 + (index * 150);

        // attrMap was already built above for type detection, reuse it for position
        if (attrMap.style) {
          // Parse style string: "left: 455.741px; top: 66.8519px;"
          const leftMatch = attrMap.style.match(/left:\s*([\d.]+)px/);
          const topMatch = attrMap.style.match(/top:\s*([\d.]+)px/);
          if (leftMatch) x = parseFloat(leftMatch[1]);
          if (topMatch) y = parseFloat(topMatch[1]);
        }

        return {
          id: nodeId,
          type: nodeType,
          x,
          y,
          properties
        };
      });

      this.nextNodeId = maxId + 1;

      // Rebuild connections from parent relationships using the ID map
      this.connections = [];
      let connectionId = 1;

      blocks.forEach((block, index) => {
        // Block.parent of -1 means it's the root/trigger node (no parent)
        // Block.parent of 0 or greater means it has a parent
        if (block.parent !== undefined && block.parent !== -1) {
          // Map old parent ID to new ID
          const sourceId = idMap.get(block.parent) !== undefined ? idMap.get(block.parent) : block.parent;
          const targetId = idMap.get(block.id) !== undefined ? idMap.get(block.id) : (index + 1);

          // Use string IDs prefixed with 'c' to avoid conflicts with node numeric IDs
          this.connections.push({
            id: `c${connectionId++}`,
            source: sourceId,
            target: targetId,
            sourcePoint: 'bottom',
            targetPoint: 'top'
          });
        }
      });

      this.nextConnectionId = connectionId;
    },

    /**
     * Map original block types back to Workflow2 node types
     * Old workflow uses human-readable names like "Appointment Completed"
     */
    mapBlockTypeToNodeType(blockType) {
      if (!blockType) return 'unknown';

      const typeMap = {
        // Triggers - Human-readable format (from old workflow)
        'New Patient': 'newpatient',
        'Appointment Scheduled': 'appointmentscheduled',
        'Appointment Broken': 'appointmentbroken',
        'Appointment Completed': 'appointmentcompleted',
        'Procedure added via Procs Quick Add': 'procaddedquickadd',
        'Pre-Auth Marked As Received': 'preauthreceived',
        '# Teamio Followups Completed': 'xfollowups',
        'A Procedure is Marked As Referral': 'proceduremarkedreferral',
        'New Website Form Submission': 'websiteform',
        'Facebook Lead Generated': 'facebooklead',
        'Google Lead Generated': 'googlelead',

        // Triggers - UPPERCASE format (legacy)
        'NEW_PATIENT': 'newpatient',
        'APPOINTMENT_SCHEDULED': 'appointmentscheduled',
        'APPOINTMENT_BROKEN': 'appointmentbroken',
        'APPOINTMENT_COMPLETED': 'appointmentcompleted',
        'PROCEDURE_ADDED_VIA_PROCS_QUICK_ADD': 'procaddedquickadd',
        'PRE_AUTH_RECEIVED': 'preauthreceived',
        'X_FOLLOWUPS': 'xfollowups',
        'PROCEDURE_MARKED_REFERRAL': 'proceduremarkedreferral',
        'WEBSITE_FORM': 'websiteform',
        'FACEBOOK_LEAD': 'facebooklead',
        'GOOGLE_LEAD': 'googlelead',
        'MANGO_VOICE': 'mangovoice',

        // Actions - Human-readable format (from old workflow)
        'Send Email': 'email',
        'Send Text Message': 'sms',
        'Assign Teamio Task': 'assigntask',
        'Show Popup': 'popup',
        'Mail Letter': 'mailletter',
        'Wait / Delay': 'wait',
        'Send a Form': 'sendform',
        'Update Kanban': 'kanban',
        'Send Slack Notification': 'slack',
        'Send Prospective Lead': 'prospectivelead',

        // Actions - UPPERCASE format (legacy)
        'EMAIL': 'email',
        'SMS': 'sms',
        'ASSIGN_TASK': 'assigntask',
        'POPUP': 'popup',
        'MAIL_LETTER': 'mailletter',
        'WAIT': 'wait',
        'SEND_FORM': 'sendform',
        'KANBAN': 'kanban',
        'SLACK': 'slack',
        'PROSPECTIVE_LEAD': 'prospectivelead'
      };

      // Try exact match first
      if (typeMap[blockType]) {
        return typeMap[blockType];
      }

      // Try lowercase match
      const lowerType = blockType.toLowerCase();
      for (const [key, value] of Object.entries(typeMap)) {
        if (key.toLowerCase() === lowerType) {
          return value;
        }
      }

      // Fallback: return lowercase version of input
      return lowerType.replace(/\s+/g, '').replace(/-/g, '');
    },

    /**
     * Extract node properties from block.attr array
     * Old workflow stores properties in attr array as objects like:
     * [{'data-send-email-properties': '...'}, {'data-blocktype': 'email'}]
     *
     * @param {Array} attrArray - The block.attr array
     * @param {string} nodeType - The node type (email, sms, etc.)
     * @param {Array} dataAttrs - The block.data array (for fallback)
     */
    extractPropertiesFromData(attrArray, nodeType, dataAttrs = []) {
      const props = {};

      // Convert attr array to a lookup map
      // attr format: [{'data-send-email-properties': '...'}, {'data-blocktype': 'email'}]
      const attrMap = {};
      if (Array.isArray(attrArray)) {
        attrArray.forEach(attrObj => {
          if (attrObj && typeof attrObj === 'object') {
            const [key, value] = Object.entries(attrObj)[0] || [];
            if (key) {
              attrMap[key] = value;
            }
          }
        });
      }

      // Helper to get from data array (fallback for some properties)
      const getDataAttr = (name) => {
        if (!Array.isArray(dataAttrs)) return null;
        const attr = dataAttrs.find(d => d && d.name === name);
        return attr ? attr.value : null;
      };

      switch (nodeType) {
        case 'email': {
          // Email properties - check both old format (attr) and new format (data)
          // Old format: attrMap['data-send-email-properties']
          // New format (Workflow2): dataAttrs with name 'emailrules'
          let emailPropsStr = attrMap['data-send-email-properties'];

          // Try Workflow2 format if old format not found
          if (!emailPropsStr) {
            emailPropsStr = getDataAttr('emailrules');
          }

          if (emailPropsStr) {
            try {
              const emailProps = JSON.parse(emailPropsStr);
              // emailProps is an array of email rule objects
              props.emailRules = Array.isArray(emailProps) ? emailProps : [];
              // Try to extract fromName and fromEmail from the first rule
              if (props.emailRules.length > 0) {
                props.fromName = props.emailRules[0].FromName || '';
                props.fromEmail = props.emailRules[0].FromEmail || '';
              }
            } catch (e) {
              console.error('Error parsing email properties:', e);
              props.emailRules = [];
            }
          } else {
            props.emailRules = [];
          }

          // Also check for fromName/fromEmail in data attrs (Workflow2 format)
          const fromNameAttr = getDataAttr('fromname');
          const fromEmailAttr = getDataAttr('fromemail');
          if (fromNameAttr) props.fromName = fromNameAttr;
          if (fromEmailAttr) props.fromEmail = fromEmailAttr;
          // Load new patient email template ID
          props.newPatientEmailTemplateId = getDataAttr('newpatientemailtemplateid') || '';
          // Load new patient email mode and provider/procedure-specific settings
          props.newPatientEmailMode = getDataAttr('newpatientemailmode') || 'all';
          try { props.newPatientEmailProviderNums = JSON.parse(getDataAttr('newpatientemailprovidernums') || '[]'); } catch(e) { props.newPatientEmailProviderNums = []; }
          try { props.newPatientEmailRules = JSON.parse(getDataAttr('newpatientemailrules') || '[]'); } catch(e) { props.newPatientEmailRules = []; }
          break;
        }

        case 'sms': {
          // SMS properties - check both old format (attr) and new format (data)
          // Old format: attrMap['data-send-sms-properties']
          // New format (Workflow2): dataAttrs with name 'smsrules'
          let smsPropsStr = attrMap['data-send-sms-properties'];

          // Try Workflow2 format if old format not found
          if (!smsPropsStr) {
            smsPropsStr = getDataAttr('smsrules');
          }

          if (smsPropsStr) {
            try {
              const smsProps = JSON.parse(smsPropsStr);
              // smsProps is an array of SMS rule objects
              props.smsRules = Array.isArray(smsProps) ? smsProps : [];
            } catch (e) {
              console.error('Error parsing SMS properties:', e);
              props.smsRules = [];
            }
          } else {
            props.smsRules = [];
          }

          // Load new patient SMS template ID
          props.newPatientSmsTemplateId = getDataAttr('newpatientsmstemplateid') || '';
          // Load new patient SMS mode and provider/procedure-specific settings
          props.newPatientMode = getDataAttr('newpatientmode') || 'all';
          try { props.newPatientProviderNums = JSON.parse(getDataAttr('newpatientprovidernums') || '[]'); } catch(e) { props.newPatientProviderNums = []; }
          try { props.newPatientRules = JSON.parse(getDataAttr('newpatientrules') || '[]'); } catch(e) { props.newPatientRules = []; }
          break;
        }

        case 'assigntask': {
          // Task properties - check both old format (attr) and new format (data)
          // Old format: attrMap['data-assign-task-properties'] or attrMap['data-assign-task-properties-{id}']
          // New format (Workflow2): dataAttrs with name 'taskrules'
          let taskPropsStr = attrMap['data-assign-task-properties'];

          // Also check for dynamic attribute names (old format)
          if (!taskPropsStr) {
            for (const key of Object.keys(attrMap)) {
              if (key.startsWith('data-assign-task-properties')) {
                taskPropsStr = attrMap[key];
                break;
              }
            }
          }

          // Try Workflow2 format if old format not found
          if (!taskPropsStr) {
            taskPropsStr = getDataAttr('taskrules');
          }

          if (taskPropsStr) {
            try {
              const taskProps = JSON.parse(taskPropsStr);
              props.taskRules = Array.isArray(taskProps) ? taskProps : [];
            } catch (e) {
              console.error('Error parsing task properties:', e);
              props.taskRules = [];
            }
          } else {
            props.taskRules = [];
          }
          break;
        }

        case 'wait': {
          // Wait properties might be in attr or data
          const waitFor = attrMap['data-waitfor'] || getDataAttr('waitfor');
          props.waitType = waitFor === 'true' ? 'duration' : 'timestamp';

          if (props.waitType === 'duration') {
            const totalSecondsStr = attrMap['data-totalseconds'] || getDataAttr('totalseconds');
            const totalSeconds = parseInt(totalSecondsStr) || 3600;
            // Convert back to appropriate unit
            if (totalSeconds >= 86400 && totalSeconds % 86400 === 0) {
              props.durationValue = totalSeconds / 86400;
              props.durationUnit = 'days';
            } else if (totalSeconds >= 3600 && totalSeconds % 3600 === 0) {
              props.durationValue = totalSeconds / 3600;
              props.durationUnit = 'hours';
            } else if (totalSeconds >= 60 && totalSeconds % 60 === 0) {
              props.durationValue = totalSeconds / 60;
              props.durationUnit = 'minutes';
            } else {
              props.durationValue = totalSeconds;
              props.durationUnit = 'seconds';
            }
          } else {
            props.waitUntil = attrMap['data-waitfortimestamp'] || getDataAttr('waitfortimestamp') || '';
          }
          break;
        }

        case 'sendform': {
          // Form properties are stored in data-send-form-properties
          const formPropsStr = attrMap['data-send-form-properties'];
          if (formPropsStr) {
            try {
              const formProps = JSON.parse(formPropsStr);
              props.formId = formProps.FormID || formProps.formId || '';
              props.newPatientOnly = formProps.NewPatientOnly || formProps.newPatientOnly || false;
              props.sendViaEmail = formProps.SendViaEmail || formProps.sendViaEmail || false;
              props.sendViaSms = formProps.SendViaSms || formProps.sendViaSms || false;
              props.emailTemplateId = formProps.EmailTemplateID || formProps.emailTemplateId || '';
              props.smsTemplateId = formProps.SmsTemplateID || formProps.smsTemplateId || '';
            } catch (e) {
              console.error('Error parsing form properties:', e);
            }
          }
          break;
        }

        case 'kanban': {
          // Kanban config round-trips as an ARRAY of rules — matching v1's format and the
          // save path's kanbanProperties shape (see nodePropertiesToDataAttributes case
          // 'kanban' and saveWorkflow's `node.type === 'kanban'` block). Workflow2 stores it
          // in the block `data` array as `kanban-properties`; v1 stored the same JSON array in
          // the block `attr` array as `data-kanban-properties`. The first rule drives the
          // edit-form state, so reopening a saved kanban node shows exactly what was configured.
          // KEEP IN SYNC with nodePropertiesToDataAttributes case 'kanban' (the serialize side).
          const rawKanban = attrMap['data-kanban-properties'] || getDataAttr('kanban-properties');
          let kanbanRules = [];
          if (rawKanban) {
            try {
              const parsed = typeof rawKanban === 'string' ? JSON.parse(rawKanban) : rawKanban;
              if (Array.isArray(parsed)) {
                kanbanRules = parsed;
              } else if (parsed && typeof parsed === 'object') {
                // Tolerate a legacy singular object by wrapping it in an array.
                kanbanRules = [parsed];
              }
            } catch (e) {
              // Malformed JSON → clean empty state, never throw.
              console.error('Error parsing kanban properties:', e);
              kanbanRules = [];
            }
          }

          const kanbanRule = kanbanRules[0] || {};
          const cardUpdate = (kanbanRule.cardUpdateProperties && typeof kanbanRule.cardUpdateProperties === 'object')
            ? kanbanRule.cardUpdateProperties
            : {};
          props.boardId = kanbanRule.kanbanBoardID || kanbanRule.boardId || attrMap['data-boardid'] || getDataAttr('boardid') || '';
          props.columnId = kanbanRule.kanbanColumnID || kanbanRule.columnId || '';
          // Normalize v1 action literals (addcard/updatecard/movecardtocolumn) to W2 vocabulary
          // (add/update/move) so the action dropdown matches and the move-only field's v-if
          // fires. LOAD path only — the save path emits W2 vocabulary verbatim (do not remap it).
          const rawCardAction = kanbanRule.cardAction || 'add';
          props.cardAction = { addcard: 'add', updatecard: 'update', movecardtocolumn: 'move' }[rawCardAction] || rawCardAction;
          props.selectedCardID = kanbanRule.selectedCardID || '';
          props.moveToColumnId = kanbanRule.moveToColumnID || kanbanRule.moveToColumnId || '';
          props.cardTitle = cardUpdate.newTitle || kanbanRule.cardTitle || '';
          props.cardDescription = cardUpdate.newDescription || kanbanRule.cardDescription || '';
          props.cardTags = cardUpdate.newTags || kanbanRule.cardTags || '';
          // Store the raw codeNum values; initializeNodeProperties maps them back to
          // procCodes objects for the multiselect when the settings panel opens (same
          // codeNum→procCodes convention the email/task cases use).
          props.codeNum = Array.isArray(kanbanRule.codeNum)
            ? kanbanRule.codeNum
            : (kanbanRule.codeNum ? [kanbanRule.codeNum] : []);
          // Preserve the structured object so the save path can re-emit it unchanged.
          props.cardUpdateProperties = cardUpdate;
          break;
        }

        case 'slack': {
          props.slackChannelId = attrMap['data-slackchannelid'] || getDataAttr('slackchannelid') || '';
          props.slackChannelName = attrMap['data-slackchannelname'] || getDataAttr('slackchannelname') || '';
          props.slackMessage = attrMap['data-slackmessage'] || getDataAttr('slackmessage') || '';
          break;
        }

        case 'xfollowups': {
          const countStr = attrMap['data-followupcount'] || getDataAttr('followupcount');
          props.followupCount = parseInt(countStr) || 1;
          break;
        }

        case 'mangovoice': {
          props.mangoEventType = attrMap['data-mangoeventtype'] || getDataAttr('mangoeventtype') || 'missed_call';
          break;
        }
      }

      return props;
    },

    async saveWorkflow() {
      if (!this.validateWorkflow()) {
        return;
      }

      // Validate workflow name
      if (!this.workflowName || !this.workflowName.trim()) {
        this.$toast.error('Please enter a name for your workflow');
        return;
      }

      // Validate workflow name format (no special characters except dashes)
      if (!this.isValidWorkflowName(this.workflowName)) {
        this.$toast.error('Workflow name cannot have whitespace, brackets, or special characters except dashes');
        return;
      }

      if (this.workflowName.length > 44) {
        this.$toast.error('Workflow name is too long. Maximum 44 characters.');
        return;
      }

      // Check for duplicate workflow name (AWS Step Functions requires unique names)
      if (this.existingWorkflows && this.existingWorkflows.length > 0) {
        const duplicateNameWorkflow = this.existingWorkflows.find(w =>
          w.Name &&
          w.Name.toLowerCase() === this.workflowName.toLowerCase() &&
          w.WorkflowID !== this.effectiveWorkflowID  // Exclude current workflow when editing
        );
        if (duplicateNameWorkflow) {
          this.$toast.error(`A workflow with the name "${this.workflowName}" already exists. Please choose a unique name.`);
          return;
        }
      }

      // For the same trigger type, only one workflow can exist
      // Check if another workflow already uses this trigger (only for new workflows)
      if (!this.isEditMode && this.existingWorkflows && this.existingWorkflows.length > 0) {
        const triggerType = this.getTriggerType();
        const workflowWithSameTrigger = this.existingWorkflows.find(w => w.TriggerOn === triggerType);
        if (workflowWithSameTrigger) {
          this.$toast.error(`A workflow with this trigger already exists ("${workflowWithSameTrigger.Name || 'Unnamed'}"). Each trigger can only be used by one workflow.`);
          return;
        }
      }

      this.isLoading = true;
      try {
        // Convert nodes to blocks format for storage (FlowyText compatible)
        const flowyData = this.convertNodesToFlowyFormat();

        // Generate STL using the AwsStlConverter
        const triggerType = this.getTriggerType();

        // Get action nodes only (exclude trigger nodes)
        const actionNodes = this.nodes.filter(n => !this.isTriggerNode(n.type));

        // Get trigger node ID to filter connections
        const triggerNode = this.nodes.find(n => this.isTriggerNode(n.type));
        const triggerNodeId = triggerNode ? triggerNode.id : null;

        // Filter connections to exclude those involving the trigger node
        // and remap to null for connections that were from/to trigger
        // This handles both directions:
        //   trigger → action (normal) and action → trigger (inverted, from old v1 Flowy format)
        const actionConnections = this.connections
          .filter(c => c.source !== triggerNodeId && c.target !== triggerNodeId) // Exclude all trigger connections
          .concat(
            // Connections FROM trigger → their targets become root action nodes
            this.connections
              .filter(c => c.source === triggerNodeId)
              .map(c => ({ ...c, source: null })), // Mark as root connections
            // Connections TO trigger → their sources also become root action nodes
            // (happens when old v1 workflows had action blocks as parent of trigger)
            this.connections
              .filter(c => c.target === triggerNodeId && c.source !== null)
              .map(c => ({ ...c, source: null, target: c.source })) // Reverse and mark as root
          );

        // Convert *Rules arrays to *Properties arrays for AWS Step Functions compatibility
        // The frontend uses *Rules (smsRules, emailRules, etc.) but the backend expects *Properties
        const nodesWithConvertedProperties = actionNodes.map(node => {
          const convertedNode = { ...node, properties: { ...node.properties } };

          // Convert based on node type
          if (node.type === 'sms' && node.properties?.smsRules) {
            // Convert smsRules - only save fields that should be persisted (matching v1 format)
            convertedNode.properties.smsProperties = node.properties.smsRules.map(rule => {
              const codeNum = rule.procCodes && Array.isArray(rule.procCodes) && rule.procCodes.length > 0
                ? rule.procCodes.map(pc => pc.value)
                : (rule.codeNum || []);

              return {
                codeNum: codeNum,
                smsTemplateID: rule.templateId,  // UI stores as templateId (no legacy fallback needed - migration deletes old field)
                scheduleWhen: rule.sendWhen,     // UI stores as sendWhen (no legacy fallback needed - migration deletes old field)
                smsText: rule.smsText
              };
            });
            // Pass through new patient SMS template ID for Appointment Scheduled trigger
            if (node.properties?.newPatientSmsTemplateId) {
              convertedNode.properties.newPatientSmsTemplateID = node.properties.newPatientSmsTemplateId;
            }
            // Pass through new patient mode, provider filter, and procedure-specific rules
            if (node.properties?.newPatientMode) {
              convertedNode.properties.newPatientMode = node.properties.newPatientMode;
            }
            if (node.properties?.newPatientProviderNums?.length > 0) {
              convertedNode.properties.newPatientProviderNums = node.properties.newPatientProviderNums;
            }
            if (node.properties?.newPatientRules?.length > 0) {
              convertedNode.properties.newPatientRulesData = node.properties.newPatientRules.map(rule => ({
                codeNum: rule.procCodes?.map(pc => pc.value) || [],
                providerNums: rule.providerNums || [],
                smsTemplateID: rule.templateId
              }));
            }
          }
          if (node.type === 'email' && node.properties?.emailRules) {
            // Convert emailRules - only save fields that should be persisted (matching v1 format)
            convertedNode.properties.emailProperties = node.properties.emailRules.map(rule => {
              const codeNum = rule.procCodes && Array.isArray(rule.procCodes) && rule.procCodes.length > 0
                ? rule.procCodes.map(pc => pc.value)
                : (rule.codeNum || []);

              return {
                codeNum: codeNum,
                emailTemplateID: rule.templateId,  // Backend expects emailTemplateID
                scheduleWhen: rule.sendWhen,  // UI stores as sendWhen (migration handles conversion)
                onlyifpreviousunopened: rule.onlyIfPreviousUnopened || 'false'
              };
            });
            // Pass through new patient email template ID for Appointment Scheduled trigger
            if (node.properties?.newPatientEmailTemplateId) {
              convertedNode.properties.newPatientEmailTemplateID = node.properties.newPatientEmailTemplateId;
            }
            // Pass through new patient email mode, provider filter, and procedure-specific rules
            if (node.properties?.newPatientEmailMode) {
              convertedNode.properties.newPatientEmailMode = node.properties.newPatientEmailMode;
            }
            if (node.properties?.newPatientEmailProviderNums?.length > 0) {
              convertedNode.properties.newPatientEmailProviderNums = node.properties.newPatientEmailProviderNums;
            }
            if (node.properties?.newPatientEmailRules?.length > 0) {
              convertedNode.properties.newPatientEmailRulesData = node.properties.newPatientEmailRules.map(rule => ({
                codeNum: rule.procCodes?.map(pc => pc.value) || [],
                providerNums: rule.providerNums || [],
                emailTemplateID: rule.templateId
              }));
            }
          }
          if (node.type === 'assigntask' && node.properties?.taskRules) {
            // Convert taskRules - only save fields that should be persisted
            convertedNode.properties.taskProperties = node.properties.taskRules.map(rule => {
              const codeNum = rule.procCodes && Array.isArray(rule.procCodes) && rule.procCodes.length > 0
                ? rule.procCodes.map(pc => pc.value)
                : (rule.codeNum || []);

              // Extract assignedToEmpID array from assignees
              // IMPORTANT: rule.assignees is the UI source of truth (Multiselect component)
              // Empty array [] means user intentionally cleared selection (not missing data!)
              // Only fall back to assignedToEmpID if assignees field doesn't exist at all
              const assignedToEmpID = (rule.assignees !== undefined && rule.assignees !== null)
                ? rule.assignees.map(a => a.EmployeeID)
                : (rule.assignedToEmpID || []);

              // DEBUG: Log multiple employee assignment
              console.log('[Workflow v2] Task Rule - Saving assignedToEmpID:', assignedToEmpID);
              console.log('[Workflow v2] Task Rule - rule.assignees:', rule.assignees);
              console.log('[Workflow v2] Task Rule - Number of employees:', assignedToEmpID.length);

              return {
                codeNum: codeNum,
                createdByEmpID: rule.createdByEmpID || null,
                createdByEmpName: rule.createdByEmpName || null,
                assignedToEmpID: assignedToEmpID,
                // ONLY use NEW field names - migration deletes legacy fields
                task: rule.description,  // UI stores as description
                taskDueDate: rule.dueDate,  // UI stores as dueDate
                wikiPageID: (rule.wikis && rule.wikis.length > 0) ? rule.wikis.map(w => w.WikiPageID) : [],
                // UI stores tags as tagsList array
                tags: (rule.tagsList && rule.tagsList.length > 0) ? rule.tagsList.join(',') : ''
              };
            });
          }
          if (node.type === 'sendform' && node.properties) {
            // For forms, match v1 format exactly
            const codeNum = node.properties.procCodes && Array.isArray(node.properties.procCodes) && node.properties.procCodes.length > 0
              ? node.properties.procCodes.map(pc => pc.value)
              : (node.properties.codeNum || []);

            convertedNode.properties.formProperties = [{
              codeNum: codeNum,
              sendVia: node.properties.sendVia || (node.properties.sendViaSms ? 'sms' : 'email'),
              templateID: node.properties.templateID || node.properties.formId,
              scheduleWhen: node.properties.scheduleWhen || node.properties.emailSendWhen || node.properties.smsSendWhen || 'immediately'
            }];
          }
          if (node.type === 'kanban' && node.properties) {
            // For kanban, match v1 format exactly
            const codeNum = node.properties.procCodes && Array.isArray(node.properties.procCodes) && node.properties.procCodes.length > 0
              ? node.properties.procCodes.map(pc => pc.value)
              : (node.properties.codeNum || []);

            convertedNode.properties.kanbanProperties = [{
              codeNum: codeNum,
              kanbanBoardID: node.properties.kanbanBoardID || node.properties.boardId,
              kanbanColumnID: node.properties.kanbanColumnID || node.properties.columnId,
              cardAction: node.properties.cardAction,
              selectedCardID: node.properties.selectedCardID,
              moveToColumnID: node.properties.moveToColumnID || node.properties.moveToColumnId,
              // Derive from the LIVE flat fields (shared helper) — NOT a stale
              // node.properties.cardUpdateProperties, which nothing assembles from the form.
              cardUpdateProperties: this.buildKanbanCardUpdateProperties(node.properties)
            }];
          }
          if (node.type === 'slack' && node.properties) {
            // For slack, save only necessary fields
            convertedNode.properties.slackProperties = {
              channelId: node.properties.channelId,
              message: node.properties.message,
              scheduleWhen: node.properties.scheduleWhen || 'immediately'
            };
          }

          return convertedNode;
        });

        const stl = AwsStlConverter.convert(
          nodesWithConvertedProperties,
          actionConnections,
          {
            siteID: this.siteID,
            workflowID: this.effectiveWorkflowID || 'undefined',
            trigger: triggerType,
            timezone: this.siteTimezone
          }
        );

        const payload = {
          SiteID: this.siteID,
          Name: this.workflowName,
          FlowyText: JSON.stringify(flowyData),
          JsonText: JSON.stringify(stl),
          TriggerOn: triggerType,
          CreatedByEmpID: this.userID,
          CreatedOn: moment().format("YYYY-MM-DD"),
          skipAlertLoading: true // Use component's own loading overlay
        };

        if (this.isEditMode) {
          payload.WorkflowID = this.effectiveWorkflowID;
          if (this.workflowData && this.workflowData.AWSStateMachineArn) {
            payload.AWSStateMachineArn = this.workflowData.AWSStateMachineArn;
          }
          await this.updateWorkflow(payload);
        } else {
          const res = await this.createWorkflow(payload);
          if (res && res.status === 200 && res.data) {
            // Store the new workflow ID
            this.workflowData = { WorkflowID: res.data };
            this.workflowSaved = true;
          }
        }

        this.isDirty = false;

        // If workflow is already active, automatically re-deploy the Step Functions definition
        // This ensures saved changes take effect immediately without requiring manual re-activation
        if (this.isEditMode && this.workflowData && this.workflowData.Active) {
          try {
            console.log('[Workflow v2] Workflow is active - auto-deploying Step Functions definition...');
            await this.enableWorkflow({ WorkflowID: this.effectiveWorkflowID });
            this.notifySaveResult('deployed', 'Workflow saved and deployed successfully!');
          } catch (deployErr) {
            console.error('Error auto-deploying workflow:', deployErr);
            this.notifySaveResult('not-deployed', 'Workflow saved, but failed to deploy. Please click Activate to deploy changes.');
            this.canActivate = true;
          }
        } else {
          // Only enable activate if workflow is not already active
          if (!this.workflowData || !this.workflowData.Active) {
            this.canActivate = true;
          }
          this.notifySaveResult('saved', 'Workflow saved successfully!');
        }
      } catch (err) {
        console.error('Error saving workflow:', err);
        this.notifySaveResult('failed', 'Error saving workflow. Please try again.');
      } finally {
        this.isLoading = false;
      }
    },

    // The one seam the redesigned builder page (redesign/WorkflowBuilderPage.vue) overrides to draw its own
    // save-result notice. Here it is exactly the toast each result has always shown. `options` is for the
    // override and is not read here.
    notifySaveResult(kind, message, options) {
      if (kind === 'deployed') this.$toast.success(message);
      else if (kind === 'not-deployed') this.$toast.warning(message);
      else if (kind === 'saved') this.$toast.success(message);
      else if (kind === 'failed') this.$toast.error(message);
    },

    isValidWorkflowName(name) {
      // Allow alphanumeric, dashes, and underscores only
      const validPattern = /^[a-zA-Z0-9_-]+$/;
      return validPattern.test(name);
    },

    async activateWorkflow() {
      const workflowID = this.effectiveWorkflowID;
      if (!workflowID) return;

      this.isLoading = true;
      try {
        await this.enableWorkflow({ WorkflowID: workflowID });
      } catch (err) {
        console.error('Error activating workflow:', err);
      } finally {
        this.isLoading = false;
      }
    },

    // ========================
    // Validation Methods
    // ========================

    validateWorkflow() {
      if (this.nodes.length === 0) {
        this.$toast.error('Please add at least one trigger to the workflow');
        return false;
      }

      // Count triggers
      const triggerNodes = this.nodes.filter(n => this.isTriggerNode(n.type));

      if (triggerNodes.length === 0) {
        this.$toast.error('Workflow must have a trigger');
        return false;
      }

      if (triggerNodes.length > 1) {
        this.$toast.error('Workflow can only have one trigger');
        return false;
      }

      // Check for at least one action node
      const actionNodes = this.nodes.filter(n => !this.isTriggerNode(n.type));
      if (actionNodes.length === 0) {
        this.$toast.error('Workflow must have at least one action. Add an action block after the trigger.');
        return false;
      }

      // SMS save-time gate (Phase 5): a workflow SMS action has no live "send", so this is where we
      // catch a message that would be carrier-blocked at send time (over the 10-segment cap →
      // billed but never delivered). Rendered with sample values (templates), same math as the
      // campaign composer + the Lambda backstop. Hard-block a too-long message; warn (non-blocking)
      // on near-cap / cost-inflated so the author can leave room before real patient data expands it.
      const smsIssues = collectWorkflowSmsIssues(this.nodes);
      if (smsIssues.blocks.length > 0) {
        this.$toast.error(`This workflow has an SMS message that's too long to send. ${smsIssues.blocks[0].message}`, { timeout: 9000 });
        return false;
      }
      if (smsIssues.warnings.length > 0 && this.$toast.info) {
        this.$toast.info(`Heads up on a workflow SMS message: ${smsIssues.warnings[0].message}`, { timeout: 8000 });
        // non-blocking — the save proceeds.
      }

      return true;
    },

    // ========================
    // Conversion Methods
    // ========================

    /**
     * Convert nodes and connections to FlowyText format
     * This maintains compatibility with the original workflow system
     */
    convertNodesToFlowyFormat() {
      // Build blocks array in flowy format
      const blocks = this.nodes.map(node => {
        // Find parent connection (where this node is the target)
        const parentConnection = this.connections.find(c => c.target === node.id);
        const parentId = parentConnection ? parentConnection.source : -1;

        // Find child connections (where this node is the source)
        const childConnections = this.connections.filter(c => c.source === node.id);
        const childWidth = childConnections.length > 1 ? childConnections.length * 240 : 0;

        return {
          id: node.id,
          parent: parentId,
          childwidth: childWidth,
          data: [
            { name: 'blockelemtype', value: this.mapTypeToBlockType(node.type) },
            { name: 'blockid', value: node.id.toString() },
            // Include node properties as data attributes
            ...this.nodePropertiesToDataAttributes(node)
          ],
          attr: [
            { class: `block blockelem create-flowy ${node.type}` },
            { style: `left: ${node.x}px; top: ${node.y}px;` }
          ]
        };
      });

      return {
        html: '', // HTML representation (not needed for Workflow2)
        blockarr: blocks,
        blocks: blocks
      };
    },

    /**
     * Map Workflow2 node types to original block type identifiers
     */
    mapTypeToBlockType(type) {
      const typeMap = {
        // Triggers
        newpatient: 'NEW_PATIENT',
        appointmentscheduled: 'APPOINTMENT_SCHEDULED',
        appointmentbroken: 'APPOINTMENT_BROKEN',
        appointmentcompleted: 'APPOINTMENT_COMPLETED',
        procaddedquickadd: 'PROCEDURE_ADDED_VIA_PROCS_QUICK_ADD',
        preauthreceived: 'PRE_AUTH_RECEIVED',
        xfollowups: 'X_FOLLOWUPS',
        proceduremarkedreferral: 'PROCEDURE_MARKED_REFERRAL',
        websiteform: 'WEBSITE_FORM',
        facebooklead: 'FACEBOOK_LEAD',
        googlelead: 'GOOGLE_LEAD',
        mangovoice: 'MANGO_VOICE',
        // Actions
        email: 'EMAIL',
        sms: 'SMS',
        assigntask: 'ASSIGN_TASK',
        popup: 'POPUP',
        mailletter: 'MAIL_LETTER',
        wait: 'WAIT',
        sendform: 'SEND_FORM',
        kanban: 'KANBAN',
        slack: 'SLACK',
        prospectivelead: 'PROSPECTIVE_LEAD'
      };
      return typeMap[type] || type.toUpperCase();
    },

    /**
     * Map a kanban board's `columns` object into the {id,name} option shape the column
     * multiselects use. Shared by the selectedBoardColumns computed and the reload hydration
     * in initializeNodeProperties so both derive columns identically.
     */
    mapBoardColumns(board) {
      if (!board || !board.columns || typeof board.columns !== 'object') return [];
      // columns is an object keyed by column ID
      return Object.values(board.columns).map(col => ({
        id: col.id,
        name: col.name || col.title || 'Unnamed Column'
      }));
    },

    /**
     * Build the kanban cardUpdateProperties object from the LIVE flat form fields
     * (cardTitle/cardDescription/cardTags v-modeled at the kanban settings panel), preserving
     * any other keys already on an existing cardUpdateProperties object (e.g. the
     * addToColumnID/moveToColumnID a v1-imported node carries). Shared by BOTH save channels —
     * the STL block in saveWorkflow (kanbanProperties → the executed Step Functions definition)
     * and the flowy serialize path (nodePropertiesToDataAttributes) — so a round-tripped node
     * that is edited then re-saved executes the values the editor shows, never stale pre-edit
     * ones, and a brand-new node's typed title/description/tags actually reach the backend
     * (otherwise cardUpdateProperties was always {} and tag alerts could never fire).
     * KEEP IN SYNC with the mirror in cypress/node-tests/workflow2-kanban-roundtrip.test.js.
     */
    buildKanbanCardUpdateProperties(props) {
      const p = props || {};
      return {
        ...(p.cardUpdateProperties || {}),
        newTitle: p.cardTitle || '',
        newDescription: p.cardDescription || '',
        newTags: p.cardTags || ''
      };
    },

    /**
     * Convert node properties to data attributes format expected by original workflow
     */
    nodePropertiesToDataAttributes(node) {
      const attrs = [];
      const props = node.properties || {};

      switch (node.type) {
        case 'email':
          if (props.fromName) attrs.push({ name: 'fromname', value: props.fromName });
          if (props.fromEmail) attrs.push({ name: 'fromemail', value: props.fromEmail });
          if (props.emailRules) attrs.push({ name: 'emailrules', value: JSON.stringify(props.emailRules) });
          if (props.newPatientEmailTemplateId) attrs.push({ name: 'newpatientemailtemplateid', value: props.newPatientEmailTemplateId });
          if (props.newPatientEmailMode) attrs.push({ name: 'newpatientemailmode', value: props.newPatientEmailMode });
          if (props.newPatientEmailProviderNums?.length > 0) attrs.push({ name: 'newpatientemailprovidernums', value: JSON.stringify(props.newPatientEmailProviderNums) });
          if (props.newPatientEmailRules?.length > 0) attrs.push({ name: 'newpatientemailrules', value: JSON.stringify(props.newPatientEmailRules) });
          break;
        case 'sms':
          if (props.smsRules) attrs.push({ name: 'smsrules', value: JSON.stringify(props.smsRules) });
          if (props.newPatientSmsTemplateId) attrs.push({ name: 'newpatientsmstemplateid', value: props.newPatientSmsTemplateId });
          if (props.newPatientMode) attrs.push({ name: 'newpatientmode', value: props.newPatientMode });
          if (props.newPatientProviderNums?.length > 0) attrs.push({ name: 'newpatientprovidernums', value: JSON.stringify(props.newPatientProviderNums) });
          if (props.newPatientRules?.length > 0) attrs.push({ name: 'newpatientrules', value: JSON.stringify(props.newPatientRules) });
          break;
        case 'assigntask':
          if (props.taskRules) attrs.push({ name: 'taskrules', value: JSON.stringify(props.taskRules) });
          break;
        case 'wait':
          attrs.push({ name: 'waittype', value: props.waitType || 'duration' });
          if (props.waitType === 'duration') {
            const seconds = this.calculateWaitSeconds(props.durationValue, props.durationUnit);
            attrs.push({ name: 'totalseconds', value: seconds.toString() });
            attrs.push({ name: 'waitfor', value: 'true' });
          } else {
            attrs.push({ name: 'waitfortimestamp', value: props.waitUntil || '' });
            attrs.push({ name: 'waitfortime', value: 'true' });
          }
          break;
        case 'sendform':
          if (props.formId) attrs.push({ name: 'formid', value: props.formId });
          attrs.push({ name: 'newpatientonly', value: props.newPatientOnly ? 'true' : 'false' });
          attrs.push({ name: 'sendviaemail', value: props.sendViaEmail ? 'true' : 'false' });
          attrs.push({ name: 'sendviasms', value: props.sendViaSms ? 'true' : 'false' });
          if (props.emailTemplateId) attrs.push({ name: 'emailtemplateid', value: props.emailTemplateId });
          if (props.smsTemplateId) attrs.push({ name: 'smstemplateid', value: props.smsTemplateId });
          break;
        case 'kanban': {
          // Round-trip the FULL kanban rule array under `data-kanban-properties`, mirroring
          // the shape saveWorkflow writes into kanbanProperties (`node.type === 'kanban'`) and
          // v1's data-kanban-properties format — not just boardid/statusid. Card
          // title/description/tags live inside cardUpdateProperties so the parse side
          // (extractPropertiesFromData case 'kanban') can restore every field on reload.
          // KEEP IN SYNC with extractPropertiesFromData case 'kanban' (the parse side).
          const kanbanCodeNum = (props.procCodes && Array.isArray(props.procCodes) && props.procCodes.length > 0)
            ? props.procCodes.map(pc => pc.value)
            : (props.codeNum || []);
          const kanbanCardUpdateProperties = this.buildKanbanCardUpdateProperties(props);
          const kanbanRule = {
            codeNum: kanbanCodeNum,
            kanbanBoardID: props.kanbanBoardID || props.boardId || '',
            kanbanColumnID: props.kanbanColumnID || props.columnId || '',
            cardAction: props.cardAction || 'add',
            selectedCardID: props.selectedCardID || '',
            moveToColumnID: props.moveToColumnID || props.moveToColumnId || '',
            cardUpdateProperties: kanbanCardUpdateProperties
          };
          attrs.push({ name: 'kanban-properties', value: JSON.stringify([kanbanRule]) });
          break;
        }
        case 'slack':
          if (props.slackChannelId) attrs.push({ name: 'slackchannelid', value: props.slackChannelId });
          if (props.slackChannelName) attrs.push({ name: 'slackchannelname', value: props.slackChannelName });
          if (props.slackMessage) attrs.push({ name: 'slackmessage', value: props.slackMessage });
          break;
        case 'xfollowups':
          if (props.followupCount) attrs.push({ name: 'followupcount', value: props.followupCount.toString() });
          break;
        case 'mangovoice':
          if (props.mangoEventType) attrs.push({ name: 'mangoeventtype', value: props.mangoEventType });
          break;
      }

      return attrs;
    },

    /**
     * Calculate total seconds for wait duration
     */
    calculateWaitSeconds(value, unit) {
      const val = parseInt(value) || 1;
      switch (unit) {
        case 'seconds': return val;
        case 'minutes': return val * 60;
        case 'hours': return val * 3600;
        case 'days': return val * 86400;
        default: return val * 3600; // Default to hours
      }
    },

    getTriggerType() {
      const triggerNode = this.nodes.find(n => this.isTriggerNode(n.type));
      if (!triggerNode) return 'unknown';
      // Map to uppercase format expected by backend
      return this.mapTypeToBlockType(triggerNode.type);
    },

    // ========================
    // UI Helper Methods
    // ========================

    goBack() {
      if (this.isDirty) {
        if (confirm('You have unsaved changes. Are you sure you want to leave?')) {
          this.$router.push({ name: 'Workflow2List' });
        }
      } else {
        this.$router.push({ name: 'Workflow2List' });
      }
    },

    resetWorkflow() {
      if (confirm('Are you sure you want to reset the workflow? All unsaved changes will be lost.')) {
        this.nodes = [];
        this.connections = [];
        this.nextNodeId = 1;
        this.nextConnectionId = 1;
        this.isDirty = false;
      }
    },

    getNodeColor(type) {
      // Check if this is a trigger - triggers get colors, actions get neutral white
      const isTrigger = this.triggers.some(t => t.type === type);

      if (!isTrigger) {
        // Actions get a clean white/neutral color like the config panel
        return '#ffffff';
      }

      // Trigger colors - all triggers use the same light blue color for consistency
      const triggerColors = {
        newpatient: '#e0f2fe',
        appointmentscheduled: '#e0f2fe',
        appointmentbroken: '#e0f2fe',
        appointmentcompleted: '#e0f2fe',
        preauthreceived: '#e0f2fe',
        xfollowups: '#e0f2fe',
        proceduremarkedreferral: '#e0f2fe',
        websiteform: '#e0f2fe',
        facebooklead: '#e0f2fe',
        googlelead: '#e0f2fe'
      };
      return triggerColors[type] || '#e0f2fe';
    },

    getNodeIcon(type) {
      const item = [...this.triggers, ...this.actions].find(i => i.type === type);
      return item ? item.icon : '';
    },

    getNodeLabel(type) {
      const item = [...this.triggers, ...this.actions].find(i => i.type === type);
      return item ? item.label : type;
    },

    getNodeDescription(type) {
      const item = [...this.triggers, ...this.actions].find(i => i.type === type);
      return item ? item.description : 'Configure the properties for this workflow node.';
    },

    nodeHasSettings(type) {
      // Only these node types have configurable settings in the panel
      const typesWithSettings = [
        'email',
        'sms',
        'assigntask',
        'wait',
        'sendform',
        'kanban',
        'slack',
        'xfollowups',
        'mangovoice'
      ];
      return typesWithSettings.includes(type);
    },

    getNodePreview(node) {
      // Return a preview text based on node configuration
      const props = node.properties || {};

      switch (node.type) {
        case 'email':
          if (props.fromEmail) return `From: ${props.fromEmail}`;
          if (props.emailRules && Array.isArray(props.emailRules) && props.emailRules.length > 0) {
            return `${props.emailRules.length} email rule(s)`;
          }
          if (props.newPatientEmailMode === 'procedure_specific' && props.newPatientEmailRules?.length > 0) {
            return `New patient: ${props.newPatientEmailRules.length} procedure rule(s)`;
          }
          if (props.newPatientEmailTemplateId) {
            return 'New patient email template';
          }
          break;
        case 'sms':
          if (props.smsRules && Array.isArray(props.smsRules) && props.smsRules.length > 0) {
            return `${props.smsRules.length} SMS rule(s)`;
          }
          if (props.newPatientMode === 'procedure_specific' && props.newPatientRules?.length > 0) {
            return `New patient: ${props.newPatientRules.length} procedure rule(s)`;
          }
          if (props.newPatientSmsTemplateId) {
            return 'New patient SMS template';
          }
          break;
        case 'assigntask':
          if (props.taskRules && Array.isArray(props.taskRules) && props.taskRules.length > 0) {
            return `${props.taskRules.length} task rule(s)`;
          }
          break;
        case 'wait':
          if (props.waitType === 'duration' && props.durationValue) {
            return `Wait ${props.durationValue} ${props.durationUnit || 'hours'}`;
          }
          if (props.waitType === 'timestamp' && props.waitUntil) return `Until: ${props.waitUntil}`;
          break;
        case 'sendform':
          if (props.formId) return 'Form configured';
          break;
        case 'kanban':
          if (props.boardId) return 'Board configured';
          break;
        case 'slack':
          if (props.slackChannelName) return `#${props.slackChannelName}`;
          break;
        case 'xfollowups':
          if (props.followupCount) return `After ${props.followupCount} followup(s)`;
          break;
        case 'mangovoice':
          if (props.mangoEventType) {
            const eventLabels = { missed_call: 'Missed Call', voicemail: 'Voicemail', call_ended: 'Call Ended' };
            return eventLabels[props.mangoEventType] || props.mangoEventType;
          }
          break;
      }

      // Triggers without settings don't need any text
      if (this.isTriggerNode(node.type) && node.type !== 'xfollowups' && node.type !== 'mangovoice') {
        return '';
      }

      return 'Click to configure...';
    },

    isTriggerNode(type) {
      return this.triggers.some(t => t.type === type);
    },

    isActionNode(type) {
      return this.actions.some(a => a.type === type);
    },

    nodeHasSettings(type) {
      // All action nodes have settings
      if (this.isActionNode(type)) return true;
      // Triggers with configurable settings
      if (type === 'xfollowups') return true;
      if (type === 'mangovoice') return true;
      // Other triggers don't have settings
      return false;
    },

    // ========================
    // Drag and Drop Methods
    // ========================

    onDragStart(event, item) {
      event.dataTransfer.setData('nodeType', item.type);
      const emptyImage = new Image();
      emptyImage.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
      event.dataTransfer.setDragImage(emptyImage, 0, 0);
      this.isDraggingFromPalette = true;
    },

    onDragOver(event) {
      event.preventDefault();
      if (!this.isDraggingFromPalette || this.nodes.length === 0) return;

      const canvasRect = this.$refs.canvas.getBoundingClientRect();
      const mouseX = event.clientX - canvasRect.left;
      const mouseY = event.clientY - canvasRect.top;

      // Find nearest node connection point within threshold
      const nodeWidth = 240;
      const nodeHeight = 120;
      let nearestSnap = null;
      let nearestDistance = this.snapThreshold;

      for (const node of this.nodes) {
        // Check bottom connection point (most common - connecting below a trigger/action)
        const bottomX = node.x + nodeWidth / 2;
        const bottomY = node.y + nodeHeight;
        const bottomDist = Math.sqrt(Math.pow(mouseX - bottomX, 2) + Math.pow(mouseY - bottomY, 2));

        if (bottomDist < nearestDistance) {
          nearestDistance = bottomDist;
          nearestSnap = { nodeId: node.id, point: 'bottom', x: bottomX, y: bottomY };
        }

        // Check top connection point (for connecting above)
        const topX = node.x + nodeWidth / 2;
        const topY = node.y;
        const topDist = Math.sqrt(Math.pow(mouseX - topX, 2) + Math.pow(mouseY - topY, 2));

        if (topDist < nearestDistance) {
          nearestDistance = topDist;
          nearestSnap = { nodeId: node.id, point: 'top', x: topX, y: topY };
        }
      }

      this.snapTarget = nearestSnap;
    },

    onDragLeave(event) {
      // Only clear snap target if actually leaving the canvas
      const canvasRect = this.$refs.canvas.getBoundingClientRect();
      if (
        event.clientX < canvasRect.left ||
        event.clientX > canvasRect.right ||
        event.clientY < canvasRect.top ||
        event.clientY > canvasRect.bottom
      ) {
        this.snapTarget = null;
        this.isDraggingFromPalette = false;
      }
    },

    onDrop(event) {
      event.preventDefault();
      const nodeType = event.dataTransfer.getData('nodeType');
      if (nodeType) {
        const canvasRect = this.$refs.canvas.getBoundingClientRect();
        let x = event.clientX - canvasRect.left - 120;
        let y = event.clientY - canvasRect.top - 60;

        // If snapping, position relative to the snap target
        if (this.snapTarget) {
          const nodeWidth = 240;
          const nodeHeight = 120;
          const gap = 60; // Gap between nodes

          if (this.snapTarget.point === 'bottom') {
            // Position new node below the target
            x = this.snapTarget.x - nodeWidth / 2;
            y = this.snapTarget.y + gap;
          } else {
            // Position new node above the target
            x = this.snapTarget.x - nodeWidth / 2;
            y = this.snapTarget.y - nodeHeight - gap;
          }
        }

        const newNodeId = this.addNode(nodeType, x, y);

        // Auto-connect if we have a snap target
        if (this.snapTarget) {
          if (this.snapTarget.point === 'bottom') {
            // Connect from target's bottom to new node's top
            this.createConnection(this.snapTarget.nodeId, newNodeId, 'bottom', 'top');
          } else {
            // Connect from new node's bottom to target's top
            this.createConnection(newNodeId, this.snapTarget.nodeId, 'bottom', 'top');
          }
        }
      }

      // Reset snap state
      this.snapTarget = null;
      this.isDraggingFromPalette = false;
    },

    addNode(type, x = 250, y = 100) {
      // Check if trying to add a trigger when one already exists in this workflow
      if (this.isTriggerNode(type)) {
        const existingTrigger = this.nodes.find(n => this.isTriggerNode(n.type));
        if (existingTrigger) {
          this.$toast.error('A workflow can only have one trigger. Please remove the existing trigger first.');
          return null;
        }

        // Check if this trigger is already used by another workflow (only for new workflows)
        if (!this.isEditMode && this.existingWorkflows && this.existingWorkflows.length > 0) {
          const triggerType = this.mapTypeToBlockType(type);
          const workflowWithSameTrigger = this.existingWorkflows.find(w => w.TriggerOn === triggerType);
          if (workflowWithSameTrigger) {
            this.$toast.error(`A workflow with this trigger already exists ("${workflowWithSameTrigger.Name || 'Unnamed'}"). Each trigger can only be used by one workflow.`);
            return null;
          }
        }
      }

      const id = this.nextNodeId++;

      // Initialize default properties based on node type
      let defaultProperties = {};
      if (type === 'mangovoice') {
        defaultProperties = {
          mangoEventType: 'missed_call',
          mangoEventTypeObj: this.mangoEventTypeOptions[0]
        };
      }

      const node = {
        id,
        type,
        x,
        y,
        properties: defaultProperties // Will store node-specific configuration
      };
      this.nodes.push(node);
      this.setDirty();
      return id;
    },

    startDrag(event, nodeId) {
      if (nodeId) {
        const node = this.nodes.find(n => n.id === nodeId);
        if (node) {
          this.draggingNode = nodeId;
          this.dragOffset = {
            x: event.clientX - node.x,
            y: event.clientY - node.y
          };
          this.selectedNode = nodeId;
          // Track start position for click vs drag detection
          this.dragStartPos = { x: event.clientX, y: event.clientY };
          this.hasDragged = false;
        }
      }
    },

    onDrag(event) {
      const canvasRect = this.$refs.canvas.getBoundingClientRect();
      this.mousePosition = {
        x: event.clientX - canvasRect.left,
        y: event.clientY - canvasRect.top
      };

      if (this.draggingNode) {
        // Check if we've moved enough to consider it a drag
        if (this.dragStartPos && !this.hasDragged) {
          const dx = Math.abs(event.clientX - this.dragStartPos.x);
          const dy = Math.abs(event.clientY - this.dragStartPos.y);
          if (dx > this.dragThreshold || dy > this.dragThreshold) {
            this.hasDragged = true;
          }
        }

        // Only move the node if we've actually dragged
        if (this.hasDragged) {
          const node = this.nodes.find(n => n.id === this.draggingNode);
          if (node) {
            node.x = event.clientX - this.dragOffset.x;
            node.y = event.clientY - this.dragOffset.y;
            this.setDirty();
          }
        }
      }
    },

    stopDrag() {
      // If we didn't drag (just clicked), open the settings panel
      // But only for action nodes or xfollowups trigger (which has settings)
      if (this.draggingNode && !this.hasDragged) {
        const node = this.nodes.find(n => n.id === this.draggingNode);
        if (node && this.nodeHasSettings(node.type)) {
          this.openNodeSettings(node);
        }
      }

      // Reset drag state
      this.draggingNode = null;
      this.dragStartPos = null;
      this.hasDragged = false;
    },

    // ========================
    // Connection Methods
    // ========================

    startConnection(nodeId, point) {
      this.connectionInProgress = {
        source: nodeId,
        sourcePoint: point,
        target: null
      };
    },

    updateTempConnection(event) {
      if (!this.connectionInProgress) return;

      const canvasRect = this.$refs.canvas.getBoundingClientRect();
      this.mousePosition = {
        x: event.clientX - canvasRect.left,
        y: event.clientY - canvasRect.top
      };

      // Check if hovering over a connection point
      const nodeElements = document.querySelectorAll('.node');
      let foundTarget = false;

      nodeElements.forEach(el => {
        const nodeId = parseInt(el.getAttribute('data-id'));
        if (nodeId !== this.connectionInProgress.source) {
          const points = el.querySelectorAll('.connection-point');
          points.forEach(point => {
            const rect = point.getBoundingClientRect();
            const isOver = (
              event.clientX >= rect.left &&
              event.clientX <= rect.right &&
              event.clientY >= rect.top &&
              event.clientY <= rect.bottom
            );

            if (isOver) {
              this.connectionInProgress.target = nodeId;
              this.connectionInProgress.targetPoint = point.classList.contains('connection-point-top') ? 'top' : 'bottom';
              foundTarget = true;
            }
          });
        }
      });

      if (!foundTarget) {
        this.connectionInProgress.target = null;
        this.connectionInProgress.targetPoint = null;
      }
    },

    finishConnection() {
      if (this.connectionInProgress && this.connectionInProgress.target) {
        this.createConnection(
          this.connectionInProgress.source,
          this.connectionInProgress.target,
          this.connectionInProgress.sourcePoint,
          this.connectionInProgress.targetPoint
        );
      }
      this.connectionInProgress = null;
    },

    createConnection(sourceId, targetId, sourcePoint = 'bottom', targetPoint = 'top') {
      const exists = this.connections.some(c =>
        c.source === sourceId && c.target === targetId
      );

      if (!exists && sourceId !== targetId) {
        this.connections.push({
          id: `c${this.nextConnectionId++}`,
          source: sourceId,
          target: targetId,
          sourcePoint,
          targetPoint
        });
        this.setDirty();
      }
    },

    getConnectionPath(connection) {
      const sourceNode = this.nodes.find(n => n.id === connection.source);
      const targetNode = this.nodes.find(n => n.id === connection.target);

      if (!sourceNode || !targetNode) {
        return '';
      }

      const sourcePoint = this.getConnectionPointCoords(sourceNode, 'bottom');
      const targetPoint = this.getConnectionPointCoords(targetNode, 'top');

      // Use a smooth S-curve that handles both vertical and diagonal connections
      const midY = (sourcePoint.y + targetPoint.y) / 2;

      return `M ${sourcePoint.x} ${sourcePoint.y}
              C ${sourcePoint.x} ${midY}
                ${targetPoint.x} ${midY}
                ${targetPoint.x} ${targetPoint.y}`;
    },

    getTempConnectionPath() {
      if (!this.connectionInProgress) return '';

      const sourceNode = this.nodes.find(n => n.id === this.connectionInProgress.source);
      if (!sourceNode) return '';

      const sourcePoint = this.getConnectionPointCoords(sourceNode, this.connectionInProgress.sourcePoint);
      const targetPoint = this.connectionInProgress.target
        ? this.getConnectionPointCoords(
            this.nodes.find(n => n.id === this.connectionInProgress.target),
            this.connectionInProgress.targetPoint || 'top'
          )
        : this.mousePosition;

      const dy = targetPoint.y - sourcePoint.y;
      const curveStrength = Math.min(Math.abs(dy) * 0.5, 80);

      return `M ${sourcePoint.x} ${sourcePoint.y}
              C ${sourcePoint.x} ${sourcePoint.y + curveStrength}
                ${targetPoint.x} ${targetPoint.y - curveStrength}
                ${targetPoint.x} ${targetPoint.y}`;
    },

    getConnectionPointCoords(node, point) {
      const nodeWidth = 240;
      const nodeHeight = 100;

      if (point === 'top') {
        return { x: node.x + nodeWidth / 2, y: node.y };
      } else {
        return { x: node.x + nodeWidth / 2, y: node.y + nodeHeight };
      }
    },

    getSnapIndicatorStyle() {
      if (!this.snapTarget) return {};

      return {
        left: `${this.snapTarget.x}px`,
        top: `${this.snapTarget.y}px`
      };
    },

    // ========================
    // Settings Panel Methods
    // ========================

    openNodeSettings(node) {
      this.selectedNode = node.id;
      // Deep clone and initialize properties
      const nodeCopy = JSON.parse(JSON.stringify(node));
      this.selectedNodeData = this.initializeNodeProperties(nodeCopy);
      this.showSettings = true;
      // Prevent body scroll when settings panel is open
      document.body.style.overflow = 'hidden';
    },

    closeSettings() {
      this.showSettings = false;
      this.selectedNodeData = {};
      // Re-enable body scroll when settings panel is closed
      document.body.style.overflow = '';
    },

    // Gate: a kanban action with no proc codes runs for EVERY procedure the trigger fires on.
    // Pure (no `this`) so it stays mirror-testable. Kanban-only — other node types never gate.
    needsEmptyProcCodesConfirm(node) {
      if (!node || node.type !== 'kanban') return false;
      const procCodes = node.properties && node.properties.procCodes;
      return !Array.isArray(procCodes) || procCodes.length === 0;
    },

    saveSettings() {
      // Double-confirm saving a kanban action with no proc codes — empty means it runs for
      // EVERY procedure this trigger fires on. Fires on every such save (incl. re-saves of an
      // already-empty node). Cancel/dismiss → stay in the panel, nothing saved.
      if (this.needsEmptyProcCodesConfirm(this.selectedNodeData)) {
        const proceed = confirm("You haven't selected any procedure codes. This Kanban action will run for EVERY procedure this trigger fires on. Are you sure?");
        if (!proceed) return;
      }
      const index = this.nodes.findIndex(n => n.id === this.selectedNode);
      if (index !== -1) {
        this.nodes.splice(index, 1, this.selectedNodeData);
        this.setDirty();
      }
      this.showSettings = false;
    },

    deleteSelectedNode() {
      if (this.selectedNode) {
        this.confirmDeleteNode(this.selectedNode);
      }
    },

    confirmDeleteNode(nodeId) {
      if (confirm('Are you sure you want to delete this block? Any configured settings will be lost.')) {
        this.deleteNode(nodeId);
        this.closeSettings();
      }
    },

    deleteNode(nodeId) {
      // Remove all connections involving this node
      this.connections = this.connections.filter(c =>
        c.source !== nodeId && c.target !== nodeId
      );
      // Remove the node
      this.nodes = this.nodes.filter(n => n.id !== nodeId);
      // Clear selection if this was the selected node
      if (this.selectedNode === nodeId) {
        this.selectedNode = null;
      }
      this.setDirty();
    },

    // ========================
    // Rule Management Methods
    // ========================

    // Email Rules
    addEmailRule() {
      if (!this.selectedNodeData.properties.emailRules) {
        this.$set(this.selectedNodeData.properties, 'emailRules', []);
      }
      // Find the default send when option object
      const defaultSendWhen = this.emailSendOptions.find(d => d.value === 'immediately') || this.emailSendOptions[0];
      this.selectedNodeData.properties.emailRules.push({
        procCodes: [], // Array of proc code objects (multi-select)
        procCode: '', // Legacy single-select field
        procCodeObj: null, // Legacy single-select object
        templateId: '',
        templateObj: null, // For multiselect v-model
        sendWhen: 'immediately',
        sendWhenObj: defaultSendWhen // For multiselect v-model
      });
    },

    removeEmailRule(index) {
      this.selectedNodeData.properties.emailRules.splice(index, 1);
    },

    // SMS Rules
    addSmsRule() {
      if (!this.selectedNodeData.properties.smsRules) {
        this.$set(this.selectedNodeData.properties, 'smsRules', []);
      }
      // Find the default send when option object
      const defaultSendWhen = this.smsSendOptions.find(d => d.value === 'immediately') || this.smsSendOptions[0];
      this.selectedNodeData.properties.smsRules.push({
        procCodes: [], // Array of proc code objects (multi-select)
        procCode: '', // Legacy single-select field
        procCodeObj: null, // Legacy single-select object
        templateId: '',
        templateObj: null, // For multiselect v-model
        sendWhen: 'immediately',
        sendWhenObj: defaultSendWhen // For multiselect v-model
      });
    },

    removeSmsRule(index) {
      this.selectedNodeData.properties.smsRules.splice(index, 1);
    },

    // New Patient SMS Rules (procedure-specific mode)
    addNewPatientSmsRule() {
      if (!this.selectedNodeData.properties.newPatientRules) {
        this.$set(this.selectedNodeData.properties, 'newPatientRules', []);
      }
      this.selectedNodeData.properties.newPatientRules.push({
        procCodes: [],
        providerObjs: [],
        providerNums: [],
        templateId: '',
        templateObj: null
      });
    },

    removeNewPatientSmsRule(index) {
      this.selectedNodeData.properties.newPatientRules.splice(index, 1);
    },

    // New Patient Email Rules (procedure-specific mode)
    addNewPatientEmailRule() {
      if (!this.selectedNodeData.properties.newPatientEmailRules) {
        this.$set(this.selectedNodeData.properties, 'newPatientEmailRules', []);
      }
      this.selectedNodeData.properties.newPatientEmailRules.push({
        procCodes: [],
        providerObjs: [],
        providerNums: [],
        templateId: '',
        templateObj: null
      });
    },

    removeNewPatientEmailRule(index) {
      this.selectedNodeData.properties.newPatientEmailRules.splice(index, 1);
    },

    // Task Rules
    addTaskRule() {
      if (!this.selectedNodeData.properties.taskRules) {
        this.$set(this.selectedNodeData.properties, 'taskRules', []);
      }
      // Find the default due date option object
      const defaultDueDate = this.dueDateOptions.find(d => d.value === 'immediately') || this.dueDateOptions[0];

      // Auto-fill task description for external triggers with helpful templates
      let defaultDescription = '';
      if (this.currentTrigger === 'MANGO_VOICE') {
        // Use placeholders that will be replaced at runtime with actual values
        defaultDescription = 'Missed call from {{callerNumber}} at {{callTime}}';
      }

      this.selectedNodeData.properties.taskRules.push({
        // Multi-select arrays (new format)
        procCodes: [], // Array of proc code objects
        assignees: [], // Array of employee objects
        wikis: [], // Array of wiki objects
        tagsList: [], // Array of tag strings
        // Single values
        description: defaultDescription,
        dueDate: 'immediately',
        dueDateObj: defaultDueDate, // For multiselect v-model
        // Legacy single-select fields (for backwards compatibility)
        procCode: '',
        procCodeObj: null,
        assigneeId: '',
        assigneeObj: null,
        wikiId: '',
        wikiObj: null,
        tags: ''
      });
    },

    removeTaskRule(index) {
      this.selectedNodeData.properties.taskRules.splice(index, 1);
    },

    // Add new tag to a task rule
    async addNewTag(newTag, rule) {
      if (!rule.tagsList) {
        this.$set(rule, 'tagsList', []);
      }

      // Add tag to the current rule
      rule.tagsList.push(newTag);

      // Persist tag to Firestore for future use across all workflows and tasks
      try {
        await this.$store.dispatch('tasks/addSiteTaskTag', {
          siteID: this.siteID,
          tagName: newTag
        });
        console.log(`Tag "${newTag}" added to task-tags collection`);
      } catch (error) {
        console.error(`Failed to persist tag "${newTag}":`, error);
        // Don't fail the UI operation if persistence fails
      }
    },

    // Slack channel name helper
    updateSlackChannelName() {
      const channel = this.slackChannels.find(c => c.id === this.selectedNodeData.properties.slackChannelId);
      if (channel) {
        this.$set(this.selectedNodeData.properties, 'slackChannelName', channel.name);
      }
    },

    formatSlackChannel(channel) {
      if (!channel) return '';
      return (channel.is_private ? '🔒 ' : '#') + channel.name;
    },

    onSlackChannelSelect(val) {
      if (val) {
        this.$set(this.selectedNodeData.properties, 'slackChannelId', val.id);
        this.$set(this.selectedNodeData.properties, 'slackChannelName', val.name);
      } else {
        this.$set(this.selectedNodeData.properties, 'slackChannelId', '');
        this.$set(this.selectedNodeData.properties, 'slackChannelName', '');
      }
    },

    onMangoEventTypeSelect(val) {
      if (val) {
        this.$set(this.selectedNodeData.properties, 'mangoEventType', val.value);
        this.$set(this.selectedNodeData.properties, 'mangoEventTypeObj', val);
      } else {
        this.$set(this.selectedNodeData.properties, 'mangoEventType', '');
        this.$set(this.selectedNodeData.properties, 'mangoEventTypeObj', null);
      }
    },

    onKanbanBoardSelect(val) {
      if (val) {
        this.$set(this.selectedNodeData.properties, 'boardId', val.id);
        // Reset column selection when board changes
        this.$set(this.selectedNodeData.properties, 'columnObj', null);
        this.$set(this.selectedNodeData.properties, 'columnId', '');
        this.$set(this.selectedNodeData.properties, 'moveToColumnObj', null);
        this.$set(this.selectedNodeData.properties, 'moveToColumnId', '');
      } else {
        this.$set(this.selectedNodeData.properties, 'boardId', '');
        this.$set(this.selectedNodeData.properties, 'columnObj', null);
        this.$set(this.selectedNodeData.properties, 'columnId', '');
      }
    },

    // Initialize node properties when opening settings
    // Also converts old workflow property formats to new format expected by multiselect components
    initializeNodeProperties(node) {
      if (!node.properties) {
        node.properties = {};
      }

      // Initialize type-specific properties
      switch (node.type) {
        case 'email':
          if (!node.properties.emailRules) node.properties.emailRules = [];
          if (!node.properties.fromName) node.properties.fromName = '';
          if (!node.properties.fromEmail) node.properties.fromEmail = '';
          // Convert old format email rules to new format
          node.properties.emailRules = node.properties.emailRules.map(rule => {
            // ALWAYS run migration and cleanup, even if new format exists
            // This ensures legacy fields are purged from memory
            const newRule = { ...rule };
            // Initialize procCodes array if not present
            if (!newRule.procCodes) newRule.procCodes = [];
            // Convert codeNum (string or array) to procCodes array of objects
            if (rule.codeNum) {
              const codeValues = Array.isArray(rule.codeNum) ? rule.codeNum : [rule.codeNum];
              newRule.procCodes = codeValues
                .filter(cv => cv) // Filter out empty values
                .map(cv => this.procCodesList.find(p => p.value === cv || String(p.value) === String(cv)))
                .filter(p => p); // Filter out null results
              // Also set legacy fields for backwards compat
              if (codeValues[0]) {
                newRule.procCode = codeValues[0];
                newRule.procCodeObj = newRule.procCodes[0] || null;
              }
            }
            // Find template object
            if (rule.emailTemplateID && !rule.templateObj) {
              const template = this.wikiTemplates.find(
                t => t.SendgridTemplateID === rule.emailTemplateID
                  || t.WikiPageID === rule.emailTemplateID
              );
              newRule.templateObj = template || null;
              newRule.templateId = rule.emailTemplateID;
            }
            // Convert scheduleWhen → sendWhen/sendWhenObj
            if (rule.scheduleWhen && !rule.sendWhenObj) {
              const sendWhen = this.emailSendOptions.find(o => o.value === rule.scheduleWhen);
              newRule.sendWhenObj = sendWhen || this.emailSendOptions[0];
              newRule.sendWhen = rule.scheduleWhen;
            }
            // Initialize sendWhen if not present (for very old workflows)
            if (!newRule.sendWhen) {
              newRule.sendWhen = 'immediately';
              newRule.sendWhenObj = this.emailSendOptions.find(o => o.value === 'immediately') || this.emailSendOptions[0];
            }

            // CLEANUP: Delete legacy fields after migration
            delete newRule.codeNum;          // Migrated to procCodes
            delete newRule.emailTemplateID;  // Migrated to templateId/templateObj
            delete newRule.scheduleWhen;     // Migrated to sendWhen/sendWhenObj

            return newRule;
          });
          // Initialize new patient email template fields
          if (!node.properties.newPatientEmailTemplateId) node.properties.newPatientEmailTemplateId = '';
          if (!node.properties.newPatientEmailTemplateObj) {
            if (node.properties.newPatientEmailTemplateId) {
              node.properties.newPatientEmailTemplateObj = this.emailTemplates.find(
                t => String(t.SendgridTemplateID) === String(node.properties.newPatientEmailTemplateId)
                  || String(t.WikiPageID) === String(node.properties.newPatientEmailTemplateId)
              ) || null;
            } else {
              node.properties.newPatientEmailTemplateObj = null;
            }
          }
          // Initialize new patient email mode (default 'all' for backward compat)
          if (!node.properties.newPatientEmailMode) node.properties.newPatientEmailMode = 'all';
          if (!node.properties.newPatientEmailProviderNums) node.properties.newPatientEmailProviderNums = [];
          if (!node.properties.newPatientEmailProviderObjs) {
            node.properties.newPatientEmailProviderObjs = (node.properties.newPatientEmailProviderNums || [])
              .map(pn => this.providersList.find(p => String(p.ProvNum) === String(pn)))
              .filter(p => p);
          }
          if (!node.properties.newPatientEmailRules) node.properties.newPatientEmailRules = [];
          node.properties.newPatientEmailRules = node.properties.newPatientEmailRules.map(rule => {
            const newRule = { ...rule };
            if (rule.codeNum && !rule.procCodes?.length) {
              const codeValues = Array.isArray(rule.codeNum) ? rule.codeNum : [rule.codeNum];
              newRule.procCodes = codeValues.map(cv => this.procCodesList.find(p => String(p.value) === String(cv))).filter(p => p);
            }
            if (!newRule.procCodes) newRule.procCodes = [];
            if (rule.providerNums && !rule.providerObjs?.length) {
              newRule.providerObjs = rule.providerNums.map(pn => this.providersList.find(p => String(p.ProvNum) === String(pn))).filter(p => p);
            }
            if (!newRule.providerObjs) newRule.providerObjs = [];
            if (!newRule.providerNums) newRule.providerNums = [];
            if (rule.emailTemplateID && !rule.templateObj) {
              newRule.templateObj = this.emailTemplates.find(t => String(t.SendgridTemplateID) === String(rule.emailTemplateID) || String(t.WikiPageID) === String(rule.emailTemplateID)) || null;
              newRule.templateId = rule.emailTemplateID;
            }
            if (!newRule.templateId) newRule.templateId = '';
            if (!newRule.templateObj) newRule.templateObj = null;
            return newRule;
          });
          break;
        case 'sms':
          // Initialize new patient SMS template fields
          if (!node.properties.newPatientSmsTemplateId) node.properties.newPatientSmsTemplateId = '';
          if (!node.properties.newPatientSmsTemplateObj) {
            if (node.properties.newPatientSmsTemplateId) {
              node.properties.newPatientSmsTemplateObj = this.smsTemplates.find(
                t => String(t.WikiPageID) === String(node.properties.newPatientSmsTemplateId)
              ) || null;
            } else {
              node.properties.newPatientSmsTemplateObj = null;
            }
          }
          // Initialize new patient SMS mode (default 'all' for backward compat)
          if (!node.properties.newPatientMode) node.properties.newPatientMode = 'all';
          if (!node.properties.newPatientProviderNums) node.properties.newPatientProviderNums = [];
          if (!node.properties.newPatientProviderObjs) {
            node.properties.newPatientProviderObjs = (node.properties.newPatientProviderNums || [])
              .map(pn => this.providersList.find(p => String(p.ProvNum) === String(pn)))
              .filter(p => p);
          }
          if (!node.properties.newPatientRules) node.properties.newPatientRules = [];
          node.properties.newPatientRules = node.properties.newPatientRules.map(rule => {
            const newRule = { ...rule };
            if (rule.codeNum && !rule.procCodes?.length) {
              const codeValues = Array.isArray(rule.codeNum) ? rule.codeNum : [rule.codeNum];
              newRule.procCodes = codeValues.map(cv => this.procCodesList.find(p => String(p.value) === String(cv))).filter(p => p);
            }
            if (!newRule.procCodes) newRule.procCodes = [];
            if (rule.providerNums && !rule.providerObjs?.length) {
              newRule.providerObjs = rule.providerNums.map(pn => this.providersList.find(p => String(p.ProvNum) === String(pn))).filter(p => p);
            }
            if (!newRule.providerObjs) newRule.providerObjs = [];
            if (!newRule.providerNums) newRule.providerNums = [];
            if (rule.smsTemplateID && !rule.templateObj) {
              newRule.templateObj = this.smsTemplates.find(t => String(t.WikiPageID) === String(rule.smsTemplateID)) || null;
              newRule.templateId = rule.smsTemplateID;
            }
            if (!newRule.templateId) newRule.templateId = '';
            if (!newRule.templateObj) newRule.templateObj = null;
            return newRule;
          });

          if (!node.properties.smsRules) node.properties.smsRules = [];
          // Convert old format SMS rules to new format
          node.properties.smsRules = node.properties.smsRules.map(rule => {
            // ALWAYS run migration and cleanup, even if new format exists
            // This ensures legacy fields are purged from memory
            const newRule = { ...rule };
            // Initialize procCodes array if not present
            if (!newRule.procCodes) newRule.procCodes = [];
            // Convert codeNum (string or array) to procCodes array of objects
            if (rule.codeNum) {
              const codeValues = Array.isArray(rule.codeNum) ? rule.codeNum : [rule.codeNum];
              newRule.procCodes = codeValues
                .filter(cv => cv) // Filter out empty values
                .map(cv => this.procCodesList.find(p => p.value === cv || String(p.value) === String(cv)))
                .filter(p => p); // Filter out null results
              // Also set legacy fields for backwards compat
              if (codeValues[0]) {
                newRule.procCode = codeValues[0];
                newRule.procCodeObj = newRule.procCodes[0] || null;
              }
            }
            // Find template object
            if (rule.smsTemplateID && !rule.templateObj) {
              const template = this.wikiTemplates.find(t => t.WikiPageID === rule.smsTemplateID);
              newRule.templateObj = template || null;
              newRule.templateId = rule.smsTemplateID;
            }
            // Convert scheduleWhen → sendWhen/sendWhenObj
            if (rule.scheduleWhen && !rule.sendWhenObj) {
              const sendWhen = this.smsSendOptions.find(o => o.value === rule.scheduleWhen);
              newRule.sendWhenObj = sendWhen || this.smsSendOptions[0];
              newRule.sendWhen = rule.scheduleWhen;
            }
            // Initialize sendWhen if not present (for very old workflows)
            if (!newRule.sendWhen) {
              newRule.sendWhen = 'immediately';
              newRule.sendWhenObj = this.smsSendOptions.find(o => o.value === 'immediately') || this.smsSendOptions[0];
            }

            // CLEANUP: Delete legacy fields after migration
            delete newRule.codeNum;        // Migrated to procCodes
            delete newRule.smsTemplateID;  // Migrated to templateId/templateObj
            delete newRule.scheduleWhen;   // Migrated to sendWhen/sendWhenObj

            return newRule;
          });
          break;
        case 'assigntask':
          if (!node.properties.taskRules) node.properties.taskRules = [];

          // Auto-add a default task rule if empty (new node)
          if (node.properties.taskRules.length === 0) {
            const defaultDueDate = this.dueDateOptions.find(d => d.value === 'immediately') || this.dueDateOptions[0];
            let defaultDescription = '';
            if (this.currentTrigger === 'MANGO_VOICE') {
              defaultDescription = 'Missed call from {{callerNumber}} at {{callTime}}';
            }
            node.properties.taskRules.push({
              procCodes: [],
              assignees: [],
              wikis: [],
              tagsList: [],
              description: defaultDescription,
              dueDate: 'immediately',
              dueDateObj: defaultDueDate,
              procCode: '',
              procCodeObj: null,
              assigneeId: '',
              assigneeObj: null,
              wikiId: '',
              wikiObj: null,
              tags: ''
            });
          }

          // Convert old format task rules to new format
          // Old format: {codeNum, assignedToEmpID, task, taskDueDate, wikiPageID, taskTag/tags}
          // New format: {procCodes[], assignees[], description, dueDateObj, dueDate, wikis[], tagsList[]}
          node.properties.taskRules = node.properties.taskRules.map(rule => {
            // ALWAYS run migration and cleanup, even if new format exists
            // This ensures legacy fields are purged from memory
            const newRule = { ...rule };

            // Initialize multi-select arrays if not present
            if (!newRule.procCodes) newRule.procCodes = [];
            if (!newRule.assignees) newRule.assignees = [];
            if (!newRule.wikis) newRule.wikis = [];
            if (!newRule.tagsList) newRule.tagsList = [];

            // Convert codeNum → procCodes array of objects
            const codeNumValue = rule.codeNum;
            if (codeNumValue) {
              const codeValues = Array.isArray(codeNumValue) ? codeNumValue : [codeNumValue];
              newRule.procCodes = codeValues
                .filter(cv => cv) // Filter out empty values
                .map(cv => this.procCodesList.find(p => p.value === cv || String(p.value) === String(cv)))
                .filter(p => p); // Filter out null results
              // Also set legacy fields for backwards compat
              if (codeValues[0]) {
                newRule.procCode = codeValues[0];
                newRule.procCodeObj = newRule.procCodes[0] || null;
              }
            }

            // Convert assignedToEmpID → assignees array of objects
            const empIdValue = rule.assignedToEmpID;
            if (empIdValue) {
              const empIds = Array.isArray(empIdValue) ? empIdValue : [empIdValue];
              newRule.assignees = empIds
                .filter(id => id) // Filter out empty values
                .map(id => this.employeesList.find(e =>
                  e.EmployeeID === id ||
                  e.EmployeeID === parseInt(id) ||
                  String(e.EmployeeID) === String(id)
                ))
                .filter(e => e); // Filter out null results
              // Also set legacy fields for backwards compat
              if (empIds[0]) {
                newRule.assigneeId = empIds[0];
                newRule.assigneeObj = newRule.assignees[0] || null;
              }
            }

            // Convert task → description
            if (rule.task && !rule.description) {
              newRule.description = rule.task;
            }

            // Convert taskDueDate → dueDate/dueDateObj (single select - unchanged)
            if (rule.taskDueDate && !rule.dueDateObj) {
              const dueDateValue = Array.isArray(rule.taskDueDate) ? rule.taskDueDate[0] : rule.taskDueDate;
              const dueDate = this.dueDateOptions.find(d => d.value === dueDateValue);
              newRule.dueDateObj = dueDate || null;
              newRule.dueDate = dueDateValue;
            }

            // Convert wikiPageID → wikis array of objects
            const wikiPageIdValue = rule.wikiPageID;
            if (wikiPageIdValue) {
              const wikiIds = Array.isArray(wikiPageIdValue) ? wikiPageIdValue : [wikiPageIdValue];
              newRule.wikis = wikiIds
                .filter(id => id) // Filter out empty values
                .map(id => this.wikisWithoutTemplates?.find(w =>
                  w.WikiPageID === id ||
                  w.WikiPageID === parseInt(id) ||
                  String(w.WikiPageID) === String(id)
                ))
                .filter(w => w); // Filter out null results
              // Also set legacy fields for backwards compat
              if (wikiIds[0]) {
                newRule.wikiId = wikiIds[0];
                newRule.wikiObj = newRule.wikis[0] || null;
              }
            }

            // Convert taskTag/tags → tagsList array of strings
            // Handle various formats: string (comma-separated), array of strings
            let tagsSource = rule.taskTag || rule.tags;
            if (tagsSource) {
              if (typeof tagsSource === 'string') {
                // Comma-separated string
                newRule.tagsList = tagsSource.split(',').map(t => t.trim()).filter(t => t);
              } else if (Array.isArray(tagsSource)) {
                // Already an array
                newRule.tagsList = tagsSource.filter(t => t);
              }
            }

            // CLEANUP: Delete legacy fields after migration
            // This prevents old values from interfering with new UI values
            delete newRule.codeNum;           // Migrated to procCodes
            delete newRule.assignedToEmpID;   // Migrated to assignees
            delete newRule.task;              // Migrated to description
            delete newRule.taskDueDate;       // Migrated to dueDate
            delete newRule.wikiPageID;        // Migrated to wikis
            delete newRule.tags;              // Migrated to tagsList
            delete newRule.taskTag;           // Migrated to tagsList

            return newRule;
          });
          break;
        case 'wait':
          if (!node.properties.waitType) node.properties.waitType = 'duration';
          if (!node.properties.durationValue) node.properties.durationValue = 1;
          if (!node.properties.durationUnit) node.properties.durationUnit = 'hours';
          // Set the unit object for multiselect
          if (!node.properties.durationUnitObj) {
            const unit = node.properties.durationUnit || 'hours';
            node.properties.durationUnitObj = this.durationUnitOptions.find(o => o.value === unit) || this.durationUnitOptions[2];
          }
          break;
        case 'sendform':
          if (!node.properties.formId) node.properties.formId = '';
          if (node.properties.newPatientOnly === undefined) node.properties.newPatientOnly = false;
          if (node.properties.sendViaEmail === undefined) node.properties.sendViaEmail = true;
          if (node.properties.sendViaSms === undefined) node.properties.sendViaSms = false;
          // Initialize Send When options for email and SMS
          if (!node.properties.emailSendWhen) node.properties.emailSendWhen = 'immediately';
          if (!node.properties.smsSendWhen) node.properties.smsSendWhen = 'immediately';
          // Set the objects for multiselect
          if (!node.properties.emailSendWhenObj) {
            const value = node.properties.emailSendWhen || 'immediately';
            node.properties.emailSendWhenObj = this.smsSendOptions.find(o => o.value === value) || this.smsSendOptions[0];
          }
          if (!node.properties.smsSendWhenObj) {
            const value = node.properties.smsSendWhen || 'immediately';
            node.properties.smsSendWhenObj = this.smsSendOptions.find(o => o.value === value) || this.smsSendOptions[0];
          }
          break;
        case 'kanban':
          if (!node.properties.boardId) node.properties.boardId = '';
          if (!node.properties.columnId) node.properties.columnId = '';
          if (!node.properties.cardAction) node.properties.cardAction = 'add';
          if (!node.properties.cardTitle) node.properties.cardTitle = '';
          if (!node.properties.cardDescription) node.properties.cardDescription = '';
          if (!node.properties.cardTags) node.properties.cardTags = '';
          if (!node.properties.moveToColumnId) node.properties.moveToColumnId = '';
          // Restore the proc-code multiselect objects from the stored codeNum values. Codes
          // present in procCodesList show their {value,text}; codes no longer in the list are
          // kept as raw {value,text:value} entries so nothing is lost (no throw). codeNum is
          // then dropped — it is re-derived from procCodes at save (email/task convention).
          if (!node.properties.procCodes || node.properties.procCodes.length === 0) {
            const kanbanCodeValues = Array.isArray(node.properties.codeNum)
              ? node.properties.codeNum
              : (node.properties.codeNum ? [node.properties.codeNum] : []);
            node.properties.procCodes = kanbanCodeValues
              .filter(cv => cv !== undefined && cv !== null && cv !== '')
              .map(cv => this.procCodesList.find(p => String(p.value) === String(cv)) || { value: String(cv), text: String(cv) });
          }
          delete node.properties.codeNum;
          // Set the card action object for multiselect
          if (!node.properties.cardActionObj) {
            const action = node.properties.cardAction || 'add';
            node.properties.cardActionObj = this.kanbanCardActions.find(a => a.value === action) || this.kanbanCardActions[0];
          }
          // Hydrate the Board / Column / Move-to-column multiselect display objects from the
          // restored IDs so the dropdowns render SELECTED (and selectedBoardColumns has a list)
          // on reload — otherwise *Obj is set only by onKanbanBoardSelect on user interaction,
          // leaving the dropdowns blank until the user re-picks the board. Board deleted since
          // save → leave unselected, no throw.
          if (!node.properties.boardObj && node.properties.boardId) {
            const board = this.kanbanBoards.find(b => b.id === node.properties.boardId);
            if (board) {
              node.properties.boardObj = board;
              const cols = this.mapBoardColumns(board);
              if (!node.properties.columnObj && node.properties.columnId) {
                node.properties.columnObj = cols.find(c => c.id === node.properties.columnId) || null;
              }
              if (!node.properties.moveToColumnObj && node.properties.moveToColumnId) {
                node.properties.moveToColumnObj = cols.find(c => c.id === node.properties.moveToColumnId) || null;
              }
            }
          }
          break;
        case 'slack':
          if (!node.properties.slackChannelId) node.properties.slackChannelId = '';
          if (!node.properties.slackMessage) node.properties.slackMessage = '';
          break;
        case 'xfollowups':
          if (!node.properties.followupCount) node.properties.followupCount = 1;
          break;
        case 'mangovoice':
          if (!node.properties.mangoEventType) node.properties.mangoEventType = 'missed_call';
          // Set the object for multiselect
          if (!node.properties.mangoEventTypeObj) {
            const eventType = node.properties.mangoEventType || 'missed_call';
            node.properties.mangoEventTypeObj = this.mangoEventTypeOptions.find(o => o.value === eventType) || this.mangoEventTypeOptions[0];
          }
          break;
      }

      return node;
    },

    setDirty() {
      this.isDirty = true;
    }
  }
};
</script>

<style scoped lang="scss">
/* WorkflowBuilder2 Styles - Modern Design */

.workflow-builder {
  display: flex;
  flex-direction: column;
  height: 100vh;
  position: relative;
  overflow: hidden;
  background-color: #f9fafb;
}

/* Header */
.header {
  background: white;
  color: #1e293b;
  padding: 0;
  border-bottom: 1px solid #e5e7eb;
  z-index: 10;
}

.header-content {
  padding: 12px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-title-area {
  display: flex;
  align-items: center;
  gap: 12px;
}

.workflow-name-input {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  width: 300px;
  transition: all 0.2s;

  &::placeholder {
    color: #94a3b8;
    font-weight: 400;
  }

  &:hover {
    border-color: #cbd5e1;
  }

  &:focus {
    outline: none;
    background: white;
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }
}

.header-subtitle {
  font-size: 18px;
  color: #1e293b;
  font-weight: 600;
}

.header-divider {
  width: 1px;
  height: 24px;
  background: #e2e8f0;
}

.back-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f1f5f9;
    color: #475569;
    border-color: #cbd5e1;
  }
}

.header-actions {
  display: flex;
  gap: 10px;
}

.btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 8px;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-reset {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #64748b;

  &:hover:not(:disabled) {
    background: #f1f5f9;
    border-color: #cbd5e1;
    color: #475569;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.btn-save {
  background: #e2e8f0;
  color: #94a3b8;
  cursor: not-allowed;
  position: relative;

  &.btn-save-active {
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    color: white;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
    animation: pulse-save 2s ease-in-out infinite;

    /* Unsaved changes indicator dot */
    &::after {
      content: '';
      position: absolute;
      top: -4px;
      right: -4px;
      width: 12px;
      height: 12px;
      background: #ef4444;
      border-radius: 50%;
      border: 2px solid white;
      animation: pulse-dot 1.5s ease-in-out infinite;
    }

    &:hover {
      box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
      transform: translateY(-1px);
      animation: none;
    }
  }

  &:disabled {
    opacity: 0.5;
  }
}

@keyframes pulse-save {
  0%, 100% {
    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
  }
  50% {
    box-shadow: 0 2px 16px rgba(99, 102, 241, 0.5);
  }
}

@keyframes pulse-dot {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.8;
  }
}

.btn-activate {
  background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);

  &:hover:not(:disabled) {
    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
    transform: translateY(-1px);
  }
}

/* Main Content */
.main-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* Left Sidebar */
.left-sidebar {
  width: 260px;
  background: #f8fafc;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
}

.sidebar-tabs {
  display: flex;
  padding: 12px 12px 0;
  gap: 4px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
}

.tab-button {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  background: transparent;
  border: none;
  border-radius: 8px 8px 0 0;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;

  &.active {
    color: #6366f1;
    background: #f8fafc;

    &::after {
      content: '';
      position: absolute;
      bottom: -1px;
      left: 0;
      right: 0;
      height: 2px;
      background: #6366f1;
    }
  }

  &:hover:not(.active) {
    color: #475569;
    background: #f1f5f9;
  }

  svg {
    width: 14px;
    height: 14px;
  }
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 12px;
}

.tab-content {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sidebar-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  cursor: grab;
  transition: all 0.2s;

  &:hover {
    border-color: #c7d2fe;
    background: #fafafe;
    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.08);
    transform: translateY(-1px);
  }

  &:active {
    cursor: grabbing;
    transform: scale(0.98);
  }
}

.sidebar-item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
  border-radius: 8px;
  color: #6366f1;

  svg {
    width: 16px;
    height: 16px;
  }
}

.sidebar-item-label {
  font-size: 13px;
  font-weight: 500;
  color: #334155;
}

/* Disabled items section */
.disabled-section {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}

.disabled-header {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
  margin-bottom: 10px;
  padding: 0 4px;
}

.sidebar-item-hint {
  font-size: 10px;
  line-height: 1.3;
  color: #b45309;
  margin-top: 2px;
  font-style: italic;
}

.sidebar-item-disabled {
  opacity: 0.7;
  cursor: not-allowed;
  background: #f8fafc;
  border-color: #e2e8f0;

  &:hover {
    background: #f8fafc;
    border-color: #e2e8f0;
    box-shadow: none;
    transform: none;
  }

  .sidebar-item-icon {
    background: #e2e8f0;
    color: #64748b;
  }

  .sidebar-item-label {
    color: #64748b;
  }
}

/* Canvas */
.canvas {
  flex: 1;
  position: relative;
  overflow: auto;
  background: #fafafa;
}

.grid-background {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
}

.connections,
.temp-connection {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
}

.connection-path {
  animation: flow 1s linear infinite;
}

@keyframes flow {
  from { stroke-dashoffset: 10; }
  to { stroke-dashoffset: 0; }
}

/* Nodes */
.node {
  position: absolute;
  width: 240px;
  min-height: 100px;
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  cursor: move;
  transition: box-shadow 0.2s, border-color 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    border-color: #d1d5db;

    .node-delete-btn {
      opacity: 1;
    }
  }

  &.selected {
    box-shadow: 0 0 0 2px #6366f1, 0 4px 12px rgba(0, 0, 0, 0.12);
    border-color: #6366f1;
  }
}

/* Node Delete Button - appears on hover */
.node-delete-btn {
  position: absolute;
  top: -10px;
  right: -10px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #fee2e2;
  border: 2px solid #fca5a5;
  border-radius: 50%;
  cursor: pointer;
  color: #dc2626;
  opacity: 0;
  transition: all 0.2s;
  z-index: 10;

  &:hover {
    background: #fecaca;
    border-color: #ef4444;
    transform: scale(1.1);
  }

  svg {
    width: 14px;
    height: 14px;
  }
}

.node-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-bottom: 1px solid #f3f4f6;
}

.node-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6366f1;
}

.node-title {
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.node-menu-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: none;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: #9ca3af;

  &:hover {
    background: #f3f4f6;
    color: #6366f1;
  }
}

.node-content {
  padding: 12px;
}

.node-preview {
  font-size: 13px;
  color: #6b7280;
}

/* Connection Points */
.connection-point {
  position: absolute;
  width: 12px;
  height: 12px;
  background: #6366f1;
  border: 2px solid white;
  border-radius: 50%;
  cursor: crosshair;
  opacity: 0;
  transition: opacity 0.2s;
}

.node:hover .connection-point {
  opacity: 1;
}

.connection-point-top {
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
}

.connection-point-bottom {
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
}

/* Snap Zone Indicator */
.snap-zone-indicator {
  position: absolute;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 100;
}

.snap-zone-pulse {
  width: 24px;
  height: 24px;
  background: rgba(99, 102, 241, 0.3);
  border: 3px solid #6366f1;
  border-radius: 50%;
  animation: snap-pulse 1s ease-in-out infinite;
}

.snap-zone-label {
  position: absolute;
  top: 30px;
  left: 50%;
  transform: translateX(-50%);
  background: #6366f1;
  color: white;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.4);
}

@keyframes snap-pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.5);
    opacity: 0.6;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Canvas Empty State */
.canvas-empty-state {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: #9ca3af;

  .empty-icon {
    margin-bottom: 16px;
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #374151;
    margin: 0 0 8px;
  }

  p {
    margin: 0;
    font-size: 14px;
  }
}

/* Settings Backdrop - semi-transparent overlay */
.settings-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 999;
  backdrop-filter: blur(2px);
  transition: opacity 0.3s ease;
}

/* Settings Sidebar */
.settings-sidebar {
  position: fixed;
  top: 0;
  right: -100vw;
  bottom: 0;
  width: 100vw; /* Full screen width */
  height: 100vh;
  background: white;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  transition: right 0.3s ease;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &.show {
    right: 0;
  }
}

.settings-content {
  flex: 1 1 0;
  min-height: 0;
  max-height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px 16px; /* Reduced padding for more horizontal space */
  padding-bottom: 90px; /* Space for absolute positioned footer */
  background: #fafbfc;

  /* Custom scrollbar styling for better visibility */
  &::-webkit-scrollbar {
    width: 12px;
  }

  &::-webkit-scrollbar-track {
    background: #e2e8f0;
    border-radius: 6px;
    margin: 8px 0;
  }

  &::-webkit-scrollbar-thumb {
    background: #94a3b8;
    border-radius: 6px;
    border: 3px solid #e2e8f0;
    min-height: 50px;

    &:hover {
      background: #64748b;
    }
  }
}

.setting-section {
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e5e7eb;

  &:last-of-type {
    border-bottom: none;
    margin-bottom: 0;
  }

  > label {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 8px;
    letter-spacing: -0.01em;
  }
}

.settings-description {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 12px;
  line-height: 1.5;
}

/* Node Type Card - Header inside settings content */
.node-type-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  border-radius: 12px;
  margin-bottom: 24px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  position: relative;

  .close-btn {
    position: absolute;
    top: 12px;
    right: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 18px;
    color: #64748b;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: #f1f5f9;
      color: #475569;
      border-color: #cbd5e1;
    }
  }
}

.node-type-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  min-width: 48px;
  border-radius: 12px;
  color: #374151;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);

  svg {
    width: 24px;
    height: 24px;
  }
}

.node-type-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.node-type-label {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.3;
}

.node-type-description {
  font-size: 13px;
  color: #64748b;
  line-height: 1.4;
}

.placeholder-text {
  font-size: 13px;
  color: #9ca3af;
  font-style: italic;
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
}

.settings-actions {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: white;
  border-top: 1px solid #e5e7eb;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.05);
  z-index: 10;
}

.delete-node-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: white;
  border: 1px solid #fca5a5;
  border-radius: 8px;
  color: #dc2626;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #fef2f2;
    border-color: #ef4444;
  }
}

.right-actions {
  display: flex;
  gap: 10px;
}

.cancel-btn {
  padding: 10px 20px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #475569;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
}

.save-btn {
  padding: 10px 20px;
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(99, 102, 241, 0.2);

  &:hover {
    background: linear-gradient(135deg, #4f46e5 0%, #4338ca 100%);
    box-shadow: 0 4px 8px rgba(99, 102, 241, 0.3);
  }
}

/* Form Elements */
.form-input,
.form-select,
.form-textarea {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  background: white;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }

  &:disabled {
    background: #f8fafc;
    color: #94a3b8;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #94a3b8;
  }
}

.form-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 12px center;
  background-repeat: no-repeat;
  background-size: 16px;
  padding-right: 36px;
}

/* Multiselect Styling for Workflow */
.workflow-multiselect {
  font-size: 14px;

  .multiselect__tags {
    min-height: 42px;
    padding: 8px 40px 0 12px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: white;
    transition: all 0.2s;
  }

  .multiselect__single {
    font-size: 14px;
    color: #1e293b;
    margin-bottom: 0;
    padding: 2px 0;
  }

  .multiselect__placeholder {
    color: #9ca3af;
    font-size: 14px;
    padding-top: 2px;
  }

  .multiselect__input {
    font-size: 14px;
    padding: 0;
    margin-bottom: 0;
  }

  .multiselect__select {
    height: 36px;
    width: 32px;
  }

  .multiselect__content-wrapper {
    border: 1px solid #d1d5db;
    border-radius: 8px;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.2);
    max-height: 250px !important;
    z-index: 99999 !important; /* Ensure dropdown appears above other elements */
    position: absolute !important;
    min-width: 100%; /* At least as wide as the input */
    width: max-content; /* Expand to fit content */
    max-width: 400px; /* Limit max width */
  }

  .multiselect__option {
    font-size: 14px;
    padding: 10px 12px;
    min-height: 36px;
    white-space: normal; /* Allow text to wrap */
    line-height: 1.4;

    &--highlight {
      background: #6366f1;
      color: white;
    }

    &--selected {
      background: #e0e7ff;
      color: #4338ca;
      font-weight: 500;

      &.multiselect__option--highlight {
        background: #6366f1;
        color: white;
      }
    }
  }

  &.multiselect--active {
    .multiselect__tags {
      border-color: #6366f1;
      box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
    }
  }
}

/* Smaller multiselect for rule tables */
.workflow-multiselect-sm {
  .multiselect__tags {
    min-height: 32px;
    padding: 4px 32px 0 8px;
  }

  .multiselect__single {
    font-size: 13px;
    padding: 0;
  }

  .multiselect__placeholder {
    font-size: 13px;
    padding-top: 0;
  }

  .multiselect__select {
    height: 30px;
    width: 28px;
  }

  .multiselect__option {
    font-size: 13px;
    padding: 8px 10px;
    min-height: 32px;
  }
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

/* Placeholder hints for dynamic variables */
.placeholder-hint {
  margin-top: 6px;
  font-size: 11px;
  color: #64748b;
  line-height: 1.4;

  i {
    color: #3b82f6;
    margin-right: 4px;
  }

  code {
    background: #f1f5f9;
    padding: 1px 4px;
    border-radius: 3px;
    font-size: 10px;
    color: #475569;
    margin: 0 2px;
  }
}

/* Calm caution shown only while the kanban Proc Codes field is empty (empty is a valid,
   match-any choice — amber, not error red). */
.proc-codes-empty-hint {
  margin-top: 6px;
  font-size: 11px;
  line-height: 1.4;
  font-style: italic;
  color: #b45309;

  i {
    margin-right: 4px;
    color: #d97706;
  }
}

/* Feature Notice */
.feature-notice {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  margin-bottom: 16px;
}

.notice-icon {
  flex-shrink: 0;
  color: #d97706;
}

.notice-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.notice-title {
  font-size: 14px;
  font-weight: 600;
  color: #92400e;
}

.notice-text {
  font-size: 13px;
  color: #a16207;
}

.feature-disabled {
  opacity: 0.5;
  pointer-events: none;
}

/* Rule Tables */
.rule-table {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: visible; /* Allow dropdowns to show outside table */
  position: relative;
}

.rule-header {
  display: flex;
  gap: 12px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e5e7eb;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.rule-row {
  display: flex;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid #f3f4f6;
  align-items: flex-start;
  background: white;

  &:last-of-type {
    border-bottom: none;
  }

  &:hover {
    background: #fafbfc;
  }
}

/* Rule table column widths */
.col-proc { flex: 1; min-width: 100px; }
.col-provider { flex: 0.8; min-width: 90px; }
.col-template { flex: 1.2; min-width: 120px; }
.col-sendwhen { flex: 1.2; min-width: 130px; }
.col-action { width: 36px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }

/* Section header with layout toggle */
.section-header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.layout-toggle {
  display: flex;
  gap: 4px;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 6px;
}

.layout-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 28px;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: white;
    color: #475569;
  }

  &.active {
    background: white;
    color: #6366f1;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
}

/* Form textarea styling */
.form-textarea {
  resize: vertical;
  min-height: 50px;
  font-family: inherit;
  line-height: 1.5;
}

.table-textarea {
  resize: vertical;
  min-height: 70px;
  font-family: inherit;
  line-height: 1.4;
  font-size: 13px;
  width: 100%;
}

.rule-row .form-input,
.rule-row .form-select {
  padding: 6px 8px;
  font-size: 13px;
}

/* Task Rules Table Layout (Horizontal) */
.task-rules-table-wrapper {
  overflow-x: auto;
  overflow-y: visible;
  margin: 0 -8px;
  padding: 0 8px;

  /* Responsive: allow horizontal scroll on smaller screens */
  @media (max-width: 1200px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

.task-rules-table {
  width: 100%;
  min-width: 800px; /* Reduced significantly - full width sidebar provides ample space */
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: visible;

  thead {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);

    th {
      padding: 12px 8px;
      font-size: 11px;
      font-weight: 600;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      text-align: left;
      border-bottom: 2px solid #e5e7eb;
      white-space: nowrap;
    }
  }

  tbody {
    tr {
      &:hover {
        background: #fafbfc;
      }

      &:not(:last-child) td {
        border-bottom: 1px solid #f3f4f6;
      }
    }

    td {
      padding: 10px 8px;
      vertical-align: top;

      .form-input {
        width: 100%;
        padding: 8px 10px;
        font-size: 13px;
      }
    }

    .action-cell {
      text-align: center;
      vertical-align: middle;
    }
  }
}

/* Multiselect styling for table cells */
.workflow-multiselect-table {
  min-height: 36px;

  .multiselect__tags {
    min-height: 36px;
    padding: 4px 30px 4px 8px;
    font-size: 13px;
  }

  .multiselect__single {
    font-size: 13px;
    margin-bottom: 0;
    padding: 0;
    line-height: 26px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .multiselect__placeholder {
    font-size: 13px;
    padding: 0;
    margin-bottom: 0;
    line-height: 26px;
  }

  .multiselect__select {
    height: 34px;
    width: 30px;
  }

  .multiselect__content-wrapper {
    min-width: 200px;
  }

  .multiselect__option {
    font-size: 13px;
    padding: 8px 12px;
    white-space: normal;
    word-break: break-word;
  }
}

/* Wide dropdown for Due Date and Send When fields.
   These hold long timing labels ("2 Days After Appointment is completed"). Options WRAP onto
   multiple lines rather than being forced onto one long line — a single line overflows the narrow
   Due Date column and gets cropped by .task-rules-table-wrapper's overflow-x. Deliberately no
   `white-space: nowrap` and no `width: max-content` here: either one re-breaks the wrap, since
   max-content sizes the panel to the longest unwrapped line. Wrapping itself comes from the base
   .workflow-multiselect / .workflow-multiselect-table rules above. */
.workflow-multiselect-wide-dropdown {
  .multiselect__content-wrapper {
    min-width: 280px;
    max-width: 300px;
  }
}

/* Task Rules Card Layout */
.task-rules-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.task-rule-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fafafa;
  overflow: visible; /* Allow dropdowns to show outside card */
  position: relative;
}

.task-rule-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
}

.rule-number {
  font-size: 13px;
  font-weight: 600;
  color: #6366f1;
}

.task-rule-card-body {
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.field-row {
  display: flex;
  gap: 12px;
}

.field-group {
  flex: 1;
  min-width: 0;
  position: relative; /* Required for proper z-index stacking */

  label {
    display: block;
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
    margin-bottom: 4px;
  }

  &.full-width {
    flex: none;
    width: 100%;
  }

  /* Ensure multiselect dropdowns appear above other elements */
  .multiselect--active {
    z-index: 99999;
  }
}

.add-rule-btn-card {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background: white;
  border: 2px dashed #d1d5db;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  color: #6366f1;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
    border-color: #6366f1;
  }
}

.remove-rule-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: none;
  border: none;
  border-radius: 4px;
  color: #9ca3af;
  cursor: pointer;

  &:hover {
    background: #fef2f2;
    color: #ef4444;
  }
}

.add-rule-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 10px;
  background: none;
  border: none;
  border-top: 1px solid #e5e7eb;
  font-size: 13px;
  font-weight: 500;
  color: #6366f1;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
  }
}

/* Delivery Options Card - for Send Form email/SMS options */
.delivery-options-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
}

.delivery-method-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e2e8f0;
  font-weight: 600;
  font-size: 14px;
  color: #475569;

  svg {
    color: #6366f1;
  }
}

/* Radio & Checkbox Groups */
.radio-group,
.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.radio-option,
.checkbox-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  input[type="radio"],
  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: #6366f1;
  }

  span {
    font-size: 14px;
    color: #374151;
  }
}

/* Duration Inputs */
.duration-inputs {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.duration-value {
  flex: 0 0 80px;
  max-width: 80px;
}

.duration-unit-select {
  flex: 1;
  min-width: 120px;
}

/* Help Text */
.help-text {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;

  &.text-warning {
    color: #b45309;
    background: #fef3c7;
    padding: 8px 12px;
    border-radius: 6px;
    margin-top: 8px;
    font-style: italic;
  }

  code {
    background: #f3f4f6;
    padding: 2px 4px;
    border-radius: 3px;
    font-size: 11px;
  }
}

/* Section Heading (bold headline for section titles) */
.section-heading {
  display: block;
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
  letter-spacing: -0.01em;
}

/* New Patient Section (outlined box with bottom divider) */
.new-patient-section {
  padding: 16px;
  margin-bottom: 20px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: #fafbfc;
}

/* New Patient Mode Radio Buttons */
.new-patient-mode-radios {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 10px 0 12px 0;
}
.np-radio-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  margin: 0;
  padding: 4px 0;
}
.form-check-label-text {
  font-size: 13px;
  color: #374151;
}

/* New Patient All Mode & Procedure Specific sections */
.new-patient-all-section,
.new-patient-procedure-section {
  padding: 12px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}
.np-field-group {
  margin-bottom: 10px;
}
.np-field-group:last-child {
  margin-bottom: 0;
}
.np-field-label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 4px;
}

/* Proc Code Section (outlined box for rules) */
.proc-code-section {
  padding: 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: #fafbfc;
}

/* Utility Classes */
.mt-2 { margin-top: 8px; }
.mt-3 { margin-top: 12px; }
.mt-4 { margin-top: 16px; }
</style>
